import {chunk, compact, groupBy, objectFromEntries, partition, noop} from '@benchling/js-utils';
import produce, {Draft} from 'immer';
import React from 'react';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assert} from 'Asserts';
import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {Measurement} from 'constants/MeasurementConstants';
import {Grid, isAccessibleGridCell, getMaxSignificantFigures} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {isSchemaFieldDatabaseBacked, MinimalSchemaField, MinimalSchemaTag} from 'folder-items/FileConstants';
import {StudyDesignType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {AccessibleGraphQLObject, accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  doesCurrentUserHaveAccessForItem,
  getCICOWarningText,
  isItemAvailableToCurrentUser,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import {
  NULL_SUBROLE_KEY,
  ORDERED_PRIMARY_EXPERIMENTAL_ROLES,
} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  getRoleLabelText,
  getRolesByRoleType,
  getRoleSymbol,
} from 'samples/experimental-roles/ExperimentalRoleUtil';
import PrimaryExperimentalRoleSymbol from 'samples/experimental-roles/PrimaryExperimentalRoleSymbol';
import {
  ContainerSchemaQuery,
  ContainerSchemaQueryVariables,
} from 'samples/plate-maps/__generated/ContainerSchemaQuery';
import {
  ReagentSchemasQuery_storableSchemas_ContainerSchemaNode as ReagentQueryContainerSchemaNode,
  ReagentSchemasQuery,
  ReagentSchemasQueryVariables,
} from 'samples/plate-maps/__generated/ReagentSchemasQuery';
import {
  SchemaSelectorsQuery,
  SchemaSelectorsQueryVariables,
} from 'samples/plate-maps/__generated/SchemaSelectorsQuery';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {
  FinalConcentrationLayer,
  TransferQuantityLayer,
} from 'samples/plate-maps/annotate-fill-plate-modal/machines/DefineTransferStepMachineConstants';
import {
  ConcentrationLayerData,
  ConcentrationSettingsWithDilutionSettings,
  createDefaultLayerSettings,
  getDefaultDilutionSettings,
} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {ConcentrationLayersProps} from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerSidebar';
import {canShowTrialSamples} from 'samples/plate-maps/integrations/procedures/PlateMapTestOrderUtils';
import MulticolorStripedCircle from 'samples/plate-maps/MulticolorStripedCircle';
import {SupportedFrozenDataParameterValue} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  AssignmentGroupValueTypeByLayer,
  BioentityContent,
  BioentitySource,
  ContainerSource,
  DisplayLayerMetadata,
  DisplayPlateMap,
  DisplayPlateMapWell,
  INACCESSIBLE_ASSIGNMENT_GROUP,
  INACCESSIBLE_GROUP_ID_SENTINEL,
  InaccessibleTestOrderInfo,
  isAccessibleTestOrderInfo,
  LiveSchematizedDisplayLayerMetadata,
  NO_ACCESS_TEST_DILUTION_FACTOR_SENTINEL,
  NULL_TEST_DILUTION_FACTOR_COLOR,
  NULL_TEST_DILUTION_FACTOR_SENTINEL,
  PatternAssignmentParameters,
  PatternDirection,
  PLATE_MAP_COLOR_PALETTE,
  PLATE_MAP_LEGEND_ICON_SIZE,
  PLATE_MAP_MAX_DATA_PARAMETER_LAYERS,
  PLATE_MAP_MAX_WELL_SOURCES,
  PlateDataParameter,
  PlateMap,
  PlateMapDisplayParams,
  PlateMapLegendItem,
  PlateMapSize,
  PlateMapWellDisplayLayer,
  PlateMapDisplayLayerAttribute,
  PlateMapWell,
  SchematizedAttribute,
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  ReagentType,
  SCHEMA_FIELD_NUMBER_WELL_COLOR,
  CONCENTRATION_LAYER_WELL_COLOR,
  SchematizedAttributeType,
  Source,
  SourceErrorsBySourceDilutionId,
  SUPPORTED_FIELD_TYPES_FOR_ANNOTATION,
  SupportedFieldType,
  SupportedFieldTypeForAnnotation,
  SupportedFieldTypeForDataParameters,
  TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL,
  TestDilutionFactor,
  TestOrderInfo,
  TestOrderInfoById,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
  WELL_VOLUME_ATTRIBUTE_SENTINEL,
  WellDisplayInfo,
  WellPosition,
  PlateProcedureParameterType,
  CONCENTRATION_ATTRIBUTE_SENTINEL,
  ConcentrationAttribute,
  PlateMapMeasurementType,
  MeasurementAttribute,
  WELL_VOLUME_WELL_COLOR,
} from 'samples/plate-maps/PlateMapConstants';
import {
  CONTAINER_SCHEMA_QUERY,
  REAGENT_SCHEMAS_QUERY,
  SCHEMA_SELECTORS_QUERY,
} from 'samples/plate-maps/PlateMapQueryUtil';
import {
  MaybeMixedMeasurement,
  MULTIPLE_VALUES_SENTINEL,
  NO_MATERIAL_INPUT_SENTINEL,
} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  getDefaultMeasurement,
  getDefaultMeasurementWithErrors,
  wellHasAnyErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {
  getErrorKeyForDisplayLayerAttribute,
  getInvalidFieldValuesFromErrors,
  getInvalidIdsFromReagentErrors,
  getWellIndicesWithErrors,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {
  PlateMapAnnotationErrorWithUUID,
  PlateMapErrorLevel,
} from 'samples/plate-maps/validation/ValidationTypes';
import {PlateSchema} from 'samples/storables/StorableSchemaConstants';
import {
  getTransferInputContentsErrorText,
  isNonNullMeasurement,
  isSourceContainerValidForTransfer,
} from 'samples/transfers/TransferUtil';
import {FIELD_TYPES, FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {isUneditableField} from 'schemas/fields/SchemaFieldUtil';
import {getInitialTag, getInitialTags, getLinksKeyFromFieldType} from 'schemas/tags/TagUtil';
import {GRAY} from 'ui-library/Colors';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {HydratedUnit} from 'units/util';
import removeNulls from 'util/removeNulls';
import setIntersection from 'util/setIntersection';
import sortedBy from 'util/sortedBy';
import {assertNever, MakeFieldsRequired, Optional} from 'util/Types';

export type SchemaSelectorOption =
  SchemaSelectorsQuery['schemaFieldSelectors'][number]['allSchemaFieldSelectorOptions'][number];

/**
 * Get the index of a well in a flattened list of wells given its 0-indexed position and the plate size.
 * Inverse of getWellPositionFromIndex
 */
export function getFlattenedWellIndex(rowIndex: number, columnIndex: number, size: PlateMapSize): number {
  return size.numColumns * rowIndex + columnIndex;
}

/**
 * Get the 0-indexed row and column position of a well given its index in a list of flattened wells.
 * Inverse of getFlattenedWellIndex
 */
export function getWellPositionFromIndex(index: number, size: PlateMapSize): WellPosition {
  const row = Math.floor(index / size.numColumns);
  const column = index % size.numColumns;
  return {row, column};
}

/**
 * Gets the indices for all wells contained in a rectangular selection between two given positions
 * on a plate map. The selection is inclusive, so if the same start and end positions are the same
 * that position is returned.
 * Indices refer to the index in a flattened array of fully populated, ordered wells.
 */
export function getWellIndicesBetweenPositions(
  start: WellPosition,
  end: WellPosition,
  size: PlateMapSize
): Array<number> {
  const indices: Array<number> = [];
  for (let row = Math.min(start.row, end.row); row <= Math.max(start.row, end.row); row++) {
    for (
      let column = Math.min(start.column, end.column);
      column <= Math.max(start.column, end.column);
      column++
    ) {
      indices.push(getFlattenedWellIndex(row, column, size));
    }
  }
  return indices;
}

/**
 * Given a plate size (num rows and num columns) generate every position in the plate from left to right, top to bottom.
 * @param size
 */
export function* wellPositionIterator(size: PlateMapSize): Generator<WellPosition> {
  const start = {row: 0, column: 0};
  const end = {row: size.numRows - 1, column: size.numColumns - 1};
  yield* wellPositionSelectionIterator(start, end, PatternDirection.RIGHT);
}

/**
 * Generate every position within the rectangular selection indicated by the given start and end positions,
 * where the start is the top left corner and the end is the bottom left corner of the rectangle.
 * If direction is DOWN, iterate from top to bottom then left to right
 * If the direction is RIGHT, iterate from left to right then top to bottom
 */
export function* wellPositionSelectionIterator(
  start: WellPosition,
  end: WellPosition,
  direction: PatternDirection
): Generator<WellPosition> {
  if (direction === PatternDirection.DOWN) {
    for (let column = start.column; column <= end.column; column++) {
      for (let row = start.row; row <= end.row; row++) {
        yield {row, column};
      }
    }
  } else {
    for (let row = start.row; row <= end.row; row++) {
      for (let column = start.column; column <= end.column; column++) {
        yield {row, column};
      }
    }
  }
}

/**
 * Given the set of assignment groups currently in a plate map, generates a color for the next assignment group.
 * Gets the next unused color from PLATE_MAP_COLOR_PALETTE in order.
 * If all the colors have been used, gets the color that has been used the least or the next sequential color.
 */
export function* getColor(assignmentGroupsById: {
  [id: string]: AssignmentGroup<AssignmentGroupValue>;
}): Generator<string, string> {
  const colors = Object.values(assignmentGroupsById).map((assignmentGroup) => assignmentGroup.color);
  const dedupedColors = new Set(colors);
  let nextColorIndex;
  while (true) {
    if (dedupedColors.size === 0) {
      nextColorIndex = 0;
    } else if (dedupedColors.size < PLATE_MAP_COLOR_PALETTE.length) {
      // If we haven't used all the colors yet, get the first one that's unused.
      nextColorIndex = nextColorIndex ?? 0;
      while (dedupedColors.has(PLATE_MAP_COLOR_PALETTE[nextColorIndex])) {
        nextColorIndex = nextColorIndex === PLATE_MAP_COLOR_PALETTE.length - 1 ? 0 : nextColorIndex + 1;
      }
    } else if (nextColorIndex == null) {
      // If all colors have been used, pick the next color starting from the least used color
      const colorCounts = colors.reduce<{[color: string]: number}>((acc, color) => {
        if (Object.keys(acc).includes(color)) {
          acc[color]++;
        } else {
          acc[color] = 1;
        }
        return acc;
      }, {});
      let leastUsedColor = '';
      let smallestCount = Number.MAX_VALUE;
      for (const [color, count] of Object.entries(colorCounts)) {
        if (count < smallestCount) {
          leastUsedColor = color;
          smallestCount = count;
        }
      }
      nextColorIndex = PLATE_MAP_COLOR_PALETTE.findIndex((color) => color === leastUsedColor);
    } else {
      // All colors have been used and the least used color was found in a previous iteration,
      // now continue in palette order.
      nextColorIndex = nextColorIndex === PLATE_MAP_COLOR_PALETTE.length - 1 ? 0 : nextColorIndex + 1;
    }
    const nextColor = PLATE_MAP_COLOR_PALETTE[nextColorIndex];
    dedupedColors.add(nextColor);
    yield nextColor;
  }
}

/**
 * Get the string ID representing the value + url combination for a schema value, usually for text schema fields.
 */
export function getValueUrlId(
  value: string | undefined | null,
  url: string | undefined | null
): string | null {
  if (value == null) {
    return null;
  }
  let id = value;
  if (url != null && url.length > 0) {
    id += `-${url}`;
  }
  return id;
}

export function getGroupIdsForSource(source: Source): Set<string> {
  if (source.type === ReagentType.SOURCE_CONTAINABLE || source.contents.length === 0) {
    return new Set([source.groupId]);
  } else {
    return new Set(source.contents.map((content) => content.groupId));
  }
}

export function getGroupIdsForReagents(
  sources: Array<Source>,
  contents: Array<BioentityContent>
): Set<string> {
  const groupIds = new Set<string>();
  for (const content of contents) {
    groupIds.add(content.groupId);
  }
  for (const source of sources) {
    const reagentGroupIds = getGroupIdsForSource(source);
    for (const reagentGroupId of reagentGroupIds) {
      groupIds.add(reagentGroupId);
    }
  }
  return groupIds;
}

/** Extract test dilution factor or sentinel for null/no access. */
function getTestDilutionFactor(testOrder: TestOrderInfo | InaccessibleTestOrderInfo): TestDilutionFactor {
  return isAccessibleTestOrderInfo(testOrder)
    ? (testOrder.dilutionFactor ?? NULL_TEST_DILUTION_FACTOR_SENTINEL)
    : NO_ACCESS_TEST_DILUTION_FACTOR_SENTINEL;
}

function testDilutionFactorToDisplay(dilutionFactor: TestDilutionFactor): string {
  if (dilutionFactor === NULL_TEST_DILUTION_FACTOR_SENTINEL) {
    return 'Null';
  }
  if (dilutionFactor === NO_ACCESS_TEST_DILUTION_FACTOR_SENTINEL) {
    return '(No access)';
  }
  return dilutionFactor.toString();
}

export function getDisplayTestDilutionFactor(testOrder: TestOrderInfo | InaccessibleTestOrderInfo): string {
  return testDilutionFactorToDisplay(getTestDilutionFactor(testOrder));
}

/**
 * Get unique dilution factors from a well's sources.
 * @param well Well to pull dilution factors for
 * @param testOrderInfoById Mapping from test order id to additional test order info (dilution)
 * @return Set of dilution factors
 */
export function getTestDilutionFactorsForWell(
  well: PlateMapWell,
  testOrderInfoById?: TestOrderInfoById
): Set<TestDilutionFactor> {
  const testOrderInfos = getTestOrderInfosForWell(well, testOrderInfoById);
  return new Set(testOrderInfos.map((info) => getTestDilutionFactor(info)));
}

export function wellHasInvalidTrialSamples(
  well: PlateMapWell,
  previewedSourceErrorsBySourceDilutionId: SourceErrorsBySourceDilutionId,
  testOrderInfoById?: TestOrderInfoById
): boolean {
  for (const source of well.sources) {
    const sourceDilutionIdentifier = getSourceDilutionIdentifier(source, testOrderInfoById);
    for (const errorsById of Object.values(previewedSourceErrorsBySourceDilutionId)) {
      if (Object.keys(errorsById).includes(sourceDilutionIdentifier)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Return whether well has more than one trial sample, across sources & existing contents.
 *
 * This actually leverages presence of test orders rather than an isTrialSample in order to be more extensible
 * as well as to properly handle the case of previously non-trial samples in plate designs later becoming
 * trial samples. Their test orders will be excluded & they should not be flagged here.
 *
 * @param well Well to test for test orders
 */
export function wellHasTooManyTrialSamples(well: PlateMapWell): boolean {
  const allSamples = [...flattenSources(well), ...well.contents];
  return allSamples.filter((sample) => (sample.testOrders?.length ?? 0) > 0).length > 1;
}

/**
 * Return whether well has more than one distinct dilution factor, across sources & existing contents.
 *
 * Returns true if well has test orders corresponding to multiple different dilution factors, whether for
 * a single trial sample or across multiple.
 *
 * @param well Well to test for multiple dilution factors
 * @param testOrderInfoById Mapping from test order id to additional test order info (dilution)
 */
export function wellHasTooManyDilutionFactors(
  well: PlateMapWell,
  testOrderInfoById?: TestOrderInfoById
): boolean {
  return getTestDilutionFactorsForWell(well, testOrderInfoById).size > 1;
}

/**
 * Get single dilution factor for source.
 *
 * Returns null if source has multiple dilution factors
 * @param source Source to pull dilution factor for
 * @param testOrderInfoById Mapping from test order id to additional test order info (dilution)
 */
export function getTestDilutionFactorForSource(
  source: Source,
  testOrderInfoById?: TestOrderInfoById
): number | null {
  const testOrderIds = getTestOrderIdsForSource(source);
  // Inaccessible test orders should not have been hydrated on sources. Filter out to satisfy typescript &
  // just to be safe.
  const testOrderInfos = getTestOrderInfosFromIds(testOrderIds, testOrderInfoById).filter(
    (info): info is TestOrderInfo => isAccessibleTestOrderInfo(info)
  );
  const dilutions = new Set(
    testOrderInfos.map(({dilutionFactor}) => dilutionFactor).filter((dF): dF is number => dF != null)
  );
  if (dilutions.size === 1) {
    return [...dilutions][0];
  }
  return null;
}

const SOURCE_DILUTION_IDENTIFIER_SEPARATOR = '-';

export function getSourceDilutionIdentifier(
  source: Source,
  testOrderInfoById: TestOrderInfoById | undefined
): string {
  const dilution = getTestDilutionFactorForSource(source, testOrderInfoById);
  if (dilution != null) {
    return `${source.id}${SOURCE_DILUTION_IDENTIFIER_SEPARATOR}${dilution}`;
  }
  return source.id;
}

// Parse source dilution identifier into just the source id
// Dilution piece is optional, no-ops if just a source id is provided
export function getSourceIdFromSourceDilutionIdentifier(identifier: string): string {
  return identifier.split(SOURCE_DILUTION_IDENTIFIER_SEPARATOR)[0];
}

/**
 * Get all test order infos from a well's sources, filling in dilution info & test order names.
 * @param well Well to pull test order infos for
 * @param testOrderInfoById Mapping from test order id to additional test order info (dilution & name)
 * @return List of test order infos (de-duplicated), with missing ones filtered out.
 */
export function getTestOrderInfosForWell(
  well: PlateMapWell,
  testOrderInfoById?: TestOrderInfoById
): Array<TestOrderInfo | InaccessibleTestOrderInfo> {
  const testOrderIds = getTestOrderIdsForWell(well);
  return getTestOrderInfosFromIds(testOrderIds, testOrderInfoById);
}

function getTestOrderInfosFromIds(
  testOrderIds: Set<string>,
  testOrderInfoById?: TestOrderInfoById
): Array<TestOrderInfo | InaccessibleTestOrderInfo> {
  return [...testOrderIds].map((testOrderId) => testOrderInfoById?.[testOrderId] ?? {invalidId: testOrderId});
}

/**
 * Extract test order ids from all a well's sources & contents.
 * @param well Well to pull test order ids for.
 * @return Set of test order ids
 */
function getTestOrderIdsForWell(well: PlateMapWell): Set<string> {
  const testOrderIds = new Set<string>();
  for (const source of well.sources) {
    getTestOrderIdsForSource(source).forEach((id) => testOrderIds.add(id));
  }
  for (const content of well.contents) {
    content.testOrders?.forEach(({id}) => testOrderIds.add(id));
  }
  return testOrderIds;
}

export function getTestOrderIdsForSource(source: Source): Set<string> {
  const testOrderIds = new Set<string>();
  if (source.type === ReagentType.SOURCE_CONTAINABLE) {
    source.testOrders?.forEach(({id}) => testOrderIds.add(id));
  } else {
    for (const content of source.contents) {
      content.testOrders?.forEach(({id}) => testOrderIds.add(id));
    }
  }
  return testOrderIds;
}

/**
 * Get assignment group id for a specific dilution factor.
 */
function getTestDilutionAssignmentGroupId(testDilutionFactor: TestDilutionFactor): string {
  return `test_dilution_${testDilutionFactor}`;
}

/**
 * Whether to show test order dilution related layers.
 *
 * We show these only when: the flag is enabled, we're in a sample testing study context, and the plate map being
 * modified/viewed is not a plate design.
 */
export function shouldShowTestOrderDilutionInfo(
  procedureStudyDesignType: StudyDesignType | null,
  plateMapType: PlateMapType | null
): boolean {
  return canShowTrialSamples(procedureStudyDesignType) && plateMapType !== PlateMapType.DESIGN;
}

interface MinMaxInfo {
  minValue: number;
  maxValue: number;
  totalAssigned: number;
}

function getMinMaxInfo(values: Array<number>): MinMaxInfo | null {
  return values.length === 0
    ? null
    : {
        minValue: Math.min(...values),
        maxValue: Math.max(...values),
        totalAssigned: values.length,
      };
}

/**
 * For the given concentration attribute, gets the min and max values of that layer across the given plate map,
 * and the number of wells in the plate map that have a value assigned to that layer.
 *
 * If no wells have a concentration value assigned for the layer, returns null.
 */
export function getMinMaxForConcentrationAttribute(
  plateMap: PlateMap,
  attribute: ConcentrationAttribute
): MinMaxInfo | null {
  const layerId = attribute.key;
  const wellConcentrationValues: Array<number> = plateMap.wells
    .map((well) => well.concentrationLayers[layerId]?.value)
    .filter((value) => value != null);
  return getMinMaxInfo(wellConcentrationValues);
}

/**
 * For the given schema field, gets the min and max values of that field across the given plate map,
 * and the number of wells in the plate map that have a value assigned to that field.
 * If no wells have a value assigned for the field, returns null.
 */
export function getMinMaxForNumberSchemaField(
  plateMap: PlateMap,
  attribute: SchematizedAttribute
): {minValue: number; maxValue: number; totalAssigned: number} | null {
  const propertyKey = attribute.schemaField.fieldType === FieldType.INTEGER ? 'integerValue' : 'floatValue';
  const allNonEmptyValues: Array<number> = plateMap.wells
    .map((well) => getSchematizedAttributeTagFromWell(well, attribute)?.[propertyKey])
    .filter((value) => value != null);
  return getMinMaxInfo(allNonEmptyValues);
}

export function getMinMaxForNumberAttribute(
  plateMap: PlateMap,
  attribute: PlateMapDisplayLayerAttribute
): {minValue: number; maxValue: number; totalAssigned: number} | null {
  const displayLayer = getDisplayLayerForAttribute(attribute);
  if (displayLayer === PlateMapWellDisplayLayer.CONCENTRATION) {
    return getMinMaxForConcentrationAttribute(plateMap, attribute as ConcentrationAttribute);
  }
  if (displayLayer === PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER) {
    return getMinMaxForNumberSchemaField(plateMap, attribute as SchematizedAttribute);
  }
  if (displayLayer === PlateMapWellDisplayLayer.MEASUREMENT) {
    return getMinMaxForMeasurementAttribute(plateMap, attribute as MeasurementAttribute);
  }
  if (displayLayer === PlateMapWellDisplayLayer.WELL_VOLUME) {
    const wellVolumeValues: Array<number> = plateMap.wells
      .map((well) => well.quantity?.value)
      .filter((value) => value != null);
    return getMinMaxInfo(wellVolumeValues);
  }
  return null;
}

/**
 * For the given attribute, gets the min and max values of that attribute across the given plate map,
 * with special handling for the case where there is only one value assigned and defaults the other value to 0.
 * If no wells have a value assigned for the attribute, returns null.
 */
function getDisplayMinMaxForNumberAttribute(
  plateMap: PlateMap,
  attribute: PlateMapDisplayLayerAttribute
): {minValue: number; maxValue: number; totalAssigned: number} | null {
  const minMax = getMinMaxForNumberAttribute(plateMap, attribute);
  if (minMax == null) {
    return null;
  }
  const {minValue, maxValue, totalAssigned} = minMax;
  let displayMin = minValue;
  let displayMax = maxValue;
  if (minValue === maxValue) {
    if (minValue > 0) {
      displayMin = 0;
    } else {
      displayMax = 0;
    }
  }
  return {minValue: displayMin, maxValue: displayMax, totalAssigned};
}

/**
 * Calculates opacity for a gradient effect based on a value's position within a range.
 *
 * Opacity falls within the range of 0.1 to 1. If the range is 0 (all values the same), returns 1.
 */
function calculateGradientOpacity(value: number, minValue: number, maxValue: number): number {
  const totalRange = maxValue - minValue;
  if (totalRange === 0) {
    return 1;
  }
  const proportion = (value - minValue) / totalRange;
  return proportion * 0.9 + 0.1;
}

/**
 * Determines if a numeric label should be hidden based on plate size or value characteristics.
 */
function getNumericDisplayLabel(displayValue: string | null | undefined, plateRows: number): string | null {
  if (plateRows > 8 || displayValue == null) {
    return null;
  }
  const shouldTruncate = displayValue.replace('.', '').length > 3;
  if (shouldTruncate) {
    const truncateLength = displayValue.includes('.') ? 4 : 3;
    return `${displayValue.substring(0, truncateLength)}...`;
  }
  return displayValue;
}

/**
 * Pull a schema tag from a plate map well for a given schematized plate map attribute.
 */
export function getSchematizedAttributeTagFromWell(
  well: PlateMapWell,
  schematizedAttribute: SchematizedAttribute
): MinimalSchemaTag | null {
  if (schematizedAttribute.type === SchematizedAttributeType.WELL_SCHEMA_FIELD) {
    return well.schemaTags[schematizedAttribute.key] ?? null;
  } else if (schematizedAttribute.type === SchematizedAttributeType.DATA_PARAMETER) {
    return well.dataParameterTags[schematizedAttribute.key] ?? null;
  } else {
    assert(false, 'Invalid schematized attribute type');
  }
}

/**
 * Gets display wells for all wells in the given plate map with display info for the given schema field attribute
 * which is a text schema field.
 */
function getTextSchemaTagDisplayWells(
  plateMap: PlateMap,
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]
    >;
  },
  schemaAttribute: SchematizedAttribute,
  errors: Array<PlateMapAnnotationErrorWithUUID>
): Array<DisplayPlateMapWell> {
  const wellsWithErrors = getWellIndicesWithErrors(errors, plateMap.size);
  return plateMap.wells.map((well, idx) => {
    const schemaTag = getSchematizedAttributeTagFromWell(well, schemaAttribute);
    const schemaTagValue = getValueUrlId(schemaTag?.value, schemaTag?.url);
    if (
      schemaTagValue == null ||
      schemaTagValue === '' ||
      !Object.keys(assignmentGroupsById).includes(schemaTagValue)
    ) {
      return well as DisplayPlateMapWell;
    }
    const groupColor = assignmentGroupsById[schemaTagValue].color;
    const colorList = groupColor ? [groupColor] : [];
    const displayInfo: WellDisplayInfo = {
      colorInfo: {type: 'solid', colors: colorList},
      label: null,
      error: wellsWithErrors.has(idx) ? PlateMapErrorLevel.ERROR : null,
    };
    return {...well, displayInfo};
  });
}

/**
 * Gets display wells for all wells in the given plate map with display info for the given schema field attribute
 * which is a single or multi select dropdown schema field.
 */
function getSelectorSchemaTagDisplayWells(
  plateMap: PlateMap,
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]
    >;
  },
  attribute: SchematizedAttribute,
  errors: Array<PlateMapAnnotationErrorWithUUID>
): Array<DisplayPlateMapWell> {
  const wellsWithErrors = getWellIndicesWithErrors(errors, plateMap.size);
  return plateMap.wells.map((well, idx) => {
    const schemaTag = getSchematizedAttributeTagFromWell(well, attribute);
    const selectedOptionIds = schemaTag?.schemaFieldSelectorOptionIds ?? [];
    if (selectedOptionIds.length === 0) {
      return well as DisplayPlateMapWell;
    }
    const groupColors = removeNulls(selectedOptionIds.map((id) => assignmentGroupsById[id]?.color));
    if (groupColors.length < 1) {
      return well as DisplayPlateMapWell;
    }
    const displayInfo: WellDisplayInfo = {
      colorInfo: {type: 'solid', colors: groupColors},
      label: null,
      error: wellsWithErrors.has(idx) ? PlateMapErrorLevel.ERROR : null,
    };
    return {...well, displayInfo};
  });
}

/**
 * Gets display wells for all wells in the given plate map with display info for the given schema field attribute
 * which is a single or multi select dropdown schema field.
 */
function getNumberSchemaTagDisplayWells(
  plateMap: PlateMap,
  attribute: SchematizedAttribute,
  errors: Array<PlateMapAnnotationErrorWithUUID>
): Array<DisplayPlateMapWell> {
  const minMax = getDisplayMinMaxForNumberAttribute(plateMap, attribute);
  const wellsWithErrors = getWellIndicesWithErrors(errors, plateMap.size);
  if (minMax == null) {
    return plateMap.wells as Array<DisplayPlateMapWell>;
  }

  const {minValue, maxValue} = minMax;
  const propertyKey = attribute.schemaField.fieldType === FieldType.INTEGER ? 'integerValue' : 'floatValue';
  return plateMap.wells.map((well, idx) => {
    const schemaTag = getSchematizedAttributeTagFromWell(well, attribute);
    const numericValue = schemaTag?.[propertyKey];
    if (schemaTag == null || numericValue == null) {
      return well as DisplayPlateMapWell;
    }
    const opacity = calculateGradientOpacity(numericValue, minValue, maxValue);
    const displayValue = schemaTag.value;
    const label = getNumericDisplayLabel(displayValue, plateMap.size.numRows);
    const displayInfo: WellDisplayInfo = {
      colorInfo: {type: 'solid', colors: [SCHEMA_FIELD_NUMBER_WELL_COLOR]},
      label,
      opacity,
      error: wellsWithErrors.has(idx) ? PlateMapErrorLevel.ERROR : null,
    };
    return {...well, displayInfo};
  });
}

/**
 * Gets display wells for all wells in the given plate map with display info for concentration layers
 */
function getConcentrationDisplayWells(
  plateMap: PlateMap,
  attribute: ConcentrationAttribute,
  errors: Array<PlateMapAnnotationErrorWithUUID>
): Array<DisplayPlateMapWell> {
  const minMax = getMinMaxForConcentrationAttribute(plateMap, attribute);
  const wellsWithErrors = getWellIndicesWithErrors(errors, plateMap.size);
  if (minMax == null) {
    return plateMap.wells as Array<DisplayPlateMapWell>;
  }

  const {minValue, maxValue} = minMax;
  const layerId = attribute.key;

  return plateMap.wells.map((well, idx) => {
    const concentrationMeasurement = well.concentrationLayers?.[layerId];
    const concentrationValue = concentrationMeasurement?.value;
    const hasError = wellsWithErrors.has(idx);

    // If well has an error, show it even if there's no valid value
    if (hasError) {
      const displayInfo: WellDisplayInfo = {
        colorInfo: {type: 'solid', colors: [CONCENTRATION_LAYER_WELL_COLOR]},
        label: null,
        opacity: 1,
        error: PlateMapErrorLevel.ERROR,
      };

      return {...well, displayInfo};
    }

    // No error and no valid measurement - return empty well
    if (concentrationMeasurement == null || concentrationValue == null) {
      return well as DisplayPlateMapWell;
    }

    // Valid measurement - show with gradient
    const opacity = calculateGradientOpacity(concentrationValue, minValue, maxValue);
    const displayValue = concentrationValue.toString();
    const label = getNumericDisplayLabel(displayValue, plateMap.size.numRows);

    const displayInfo: WellDisplayInfo = {
      colorInfo: {type: 'solid', colors: [CONCENTRATION_LAYER_WELL_COLOR]},
      label,
      opacity,
      error: null,
    };

    return {...well, displayInfo};
  });
}

/**
 * Merges a list of measurements into a single MaybeMixedMeasurement.
 *
 * If all values are the same, returns that value. If all units are the same, returns that unit.
 * Otherwise returns MULTIPLE_VALUES_SENTINEL for mixed values/units.
 */
function mergeMeasurements(measurements: Array<Measurement>): MaybeMixedMeasurement {
  const measurementValues: Set<number | null> = new Set();
  const measurementUnitSymbols: Set<string | null> = new Set();
  const measurementUnits = new Array<MeasurementUnit | null>();

  for (const measurement of measurements) {
    measurementValues.add(measurement.value);
    const measurementUnitSymbol = measurement.unit?.symbol ?? null;
    measurementUnitSymbols.add(measurementUnitSymbol);
    measurementUnits.push(measurement.unit);
  }

  if (measurementValues.size === 0 || measurementUnitSymbols.size === 0) {
    return {value: null, unit: null};
  }

  const measurementValue =
    measurementValues.size === 1 ? [...measurementValues][0] : MULTIPLE_VALUES_SENTINEL;
  const measurementUnit = measurementUnitSymbols.size === 1 ? measurementUnits[0] : MULTIPLE_VALUES_SENTINEL;

  return {value: measurementValue, unit: measurementUnit};
}

/**
 * Gets the measurement from the given well, aggregating across bioentities that match the reagentId

 * If values/units are consistent, returns that value/unit. Otherwise returns MULTIPLE_VALUES_SENTINEL.
 */
function getMeasurementFromWell(
  well: PlateMapWell,
  measurementType: PlateMapMeasurementType,
  reagentId: string
): MaybeMixedMeasurement {
  let measurements: Array<Measurement> = [];
  if (measurementType === PlateMapMeasurementType.TRANSFER_QUANTITY) {
    measurements = well.sources
      .filter((source) => getGroupIdsForSource(source).has(reagentId))
      .map((source) => source.transferQuantity.measurement);
  } else if (measurementType === PlateMapMeasurementType.FINAL_CONCENTRATION) {
    measurements = flattenSources(well)
      .filter((source) => source.groupId === reagentId)
      .map((source) => source.finalConcentration.measurement);
  } else if (measurementType === PlateMapMeasurementType.CONTENT_CONCENTRATION) {
    measurements = well.contents
      .filter((content) => content.groupId === reagentId)
      .map((content) => content.finalConcentration.measurement);
  } else {
    assert(false, `Unexpected measurement type: ${assertNever(measurementType)}`);
  }
  return mergeMeasurements(measurements);
}

/**
 * Given a measurement attribute, get matching reagent source measurement values and return the min/max info
 */
function getMinMaxForMeasurementAttribute(
  plateMap: PlateMap,
  attribute: MeasurementAttribute
): MinMaxInfo | null {
  const reagentId = attribute.key;
  const measurementType = attribute.type;
  const measurementValues = plateMap.wells
    .map((well) => getMeasurementFromWell(well, measurementType, reagentId).value)
    .filter((value) => value != null && value !== MULTIPLE_VALUES_SENTINEL);
  return getMinMaxInfo(measurementValues);
}

/**
 * Gets display info for a measurement well based on its state.
 */
function getMeasurementWellDisplayInfo(
  well: PlateMapWell,
  attribute: MeasurementAttribute,
  reagentId: string,
  minMax: MinMaxInfo | null,
  plateMap: PlateMap
): WellDisplayInfo | null {
  // For content concentration, check contents instead of sources
  const hasMatchingReagent =
    attribute.type === PlateMapMeasurementType.CONTENT_CONCENTRATION
      ? well.contents.some((content) => content.groupId === reagentId)
      : flattenSources(well).some((source) => source.groupId === reagentId);

  // Wells without matching reagent show white (return null to show well as-is)
  if (!hasMatchingReagent) {
    return {
      colorInfo: {type: 'solid', colors: [GRAY]},
      label: null,
      opacity: 1,
      error: null,
    };
  }

  const measurement = getMeasurementFromWell(well, attribute.type, reagentId);
  const measurementValue = measurement.value;

  // No measurement value - return null to show well as-is (white)
  if (measurementValue == null) {
    return null;
  }

  // Multiple values - show left-to-right gradient with "..." label
  if (measurementValue === MULTIPLE_VALUES_SENTINEL) {
    return {
      colorInfo: {type: 'gradient', color: attribute.color},
      label: '...',
      opacity: 1,
      error: null,
    };
  }

  // minMax is null if there are no wells with valid measurement value after filtering nulls and MULTIPLE_VALUES_SENTINEL
  if (minMax == null) {
    return null;
  }

  // Single measurement value with gradient (minMax is guaranteed to be non-null here)
  const {minValue, maxValue} = minMax;
  const opacity = calculateGradientOpacity(measurementValue, minValue, maxValue);
  const displayValue = measurementValue.toString();
  const label = getNumericDisplayLabel(displayValue, plateMap.size.numRows);

  return {
    colorInfo: {type: 'solid', colors: [attribute.color]},
    label,
    opacity,
    error: null,
  };
}

/**
 *  Gets display wells for all wells in the given plate map with display info for measurements
 *
 *  Wells without matching reagent (source or content) show white. Wells with multiple values show a gradient with "...".
 *  Wells with single values show color with opacity based on the measurement value range.
 */
function getMeasurementDisplayWells(
  plateMap: PlateMap,
  attribute: MeasurementAttribute
): Array<DisplayPlateMapWell> {
  const reagentId = attribute.key;
  const minMax = getMinMaxForMeasurementAttribute(plateMap, attribute);

  return plateMap.wells.map((well) => {
    const displayInfo = getMeasurementWellDisplayInfo(well, attribute, reagentId, minMax, plateMap);
    return displayInfo ? {...well, displayInfo} : (well as DisplayPlateMapWell);
  });
}

function getColorDisplayInfo(
  well: PlateMapWell,
  assignmentGroupsById: {[id: string]: ReagentAssignmentGroup}
): WellDisplayInfo {
  const displayInfo: WellDisplayInfo = {
    colorInfo: {type: 'solid', colors: []},
    label: null,
  };
  const groupIds = [...getGroupIdsForReagents(well.sources, well.contents)];
  const assignmentGroupIds = Object.keys(assignmentGroupsById);
  if (!groupIds.every((id) => assignmentGroupIds.includes(id))) {
    return displayInfo;
  }
  displayInfo.colorInfo = {
    type: 'solid',
    colors: groupIds.map((groupId) => assignmentGroupsById[groupId].color),
  };
  return displayInfo;
}

/**
 * Gets display wells for all wells in the given plate map with reagent display info.
 * If the well has no reagents, the display info is null.
 * @param plateMap The plate map to display
 * @param assignmentGroupsById A mapping of assignment group ids to assignment groups.
 * @param previewedWellIndices Set of well indices currently selected for preview. Controls showing too many
 * trial samples warning only when previewing sources.
 * @param plateMapType Type of the plate map; trial sample warnings are hidden for plate designs
 * @package
 */
export function getReagentDisplayWells({
  plateMap,
  assignmentGroupsById,
  errors,
  previewedSourceErrorsBySourceDilutionId,
  previewedWellIndices,
  plateMapType,
}: {
  plateMap: PlateMap;
  assignmentGroupsById: {[id: string]: ReagentAssignmentGroup};
  errors: Array<PlateMapAnnotationErrorWithUUID>;
  previewedSourceErrorsBySourceDilutionId: SourceErrorsBySourceDilutionId;
  previewedWellIndices?: Set<number>;
  plateMapType?: PlateMapType | null;
}): Array<DisplayPlateMapWell> {
  const invalidSourceIds = getInvalidIdsFromReagentErrors(errors.map(({error}) => error));
  return plateMap.wells.map((well, index) => {
    if (well.sources.length === 0 && well.contents.length === 0) {
      return well as DisplayPlateMapWell;
    }
    // Display a label if there is exactly one distinct label across all sources in the well.
    let label: string | null = null;
    const allLabels = well.sources.map((reagent) => reagent.label).filter((label) => label != null);
    // If all non-null labels are the same, display the label.
    if (allLabels.length > 0 && allLabels.every((label) => label === allLabels[0])) {
      label = `${allLabels[0]}`;
    }
    // Display an error if any measurement on the well or any sources or contents have an error, or if there
    // are too many sources in the well.
    const wellSourceIds = well.sources.map((reagent) => reagent.id);
    const hasErrorSources = setIntersection(invalidSourceIds, new Set(wellSourceIds)).size > 0;
    let errorLevel = null;
    if (
      wellHasAnySourceContainersWithNoContents(well) ||
      wellHasTooManySources(well) ||
      wellHasAnyErrors(well) ||
      hasErrorSources ||
      wellHasInvalidTrialSamples(
        well,
        previewedSourceErrorsBySourceDilutionId,
        plateMap.testOrderDilutionInfoById
      )
    ) {
      errorLevel = PlateMapErrorLevel.ERROR;
    } else if (
      (wellHasTooManyTrialSamples(well) ||
        wellHasTooManyDilutionFactors(well, plateMap.testOrderDilutionInfoById)) &&
      (previewedWellIndices ?? new Set()).has(index) &&
      plateMapType !== PlateMapType.DESIGN
    ) {
      // Display a warning if well reagents are being previewed & well has too many trial samples, or too many
      // dilution factors, outside a plate design.
      errorLevel = PlateMapErrorLevel.WARNING;
    }

    return {
      ...well,
      displayInfo: {
        ...getColorDisplayInfo(well, assignmentGroupsById),
        label,
        error: errorLevel,
      },
    };
  });
}

/**
 * Gets display wells for all wells in the given plate map with test dilution factor display info.
 * If the well has no sources with test dilution factors, the display info is null.
 * @param plateMap The plate map to display
 * @param assignmentGroupsById A mapping of assignment group ids to assignment groups.
 */
function getTestDilutionFactorDisplayWells(
  plateMap: PlateMap,
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR]
    >;
  }
): Array<DisplayPlateMapWell> {
  const assignmentGroupIds = Object.keys(assignmentGroupsById);
  return plateMap.wells.map((well) => {
    const dilutionFactors = [...getTestDilutionFactorsForWell(well, plateMap.testOrderDilutionInfoById)];
    const groupIds = dilutionFactors.map((dilutionFactor) =>
      getTestDilutionAssignmentGroupId(dilutionFactor)
    );
    const areAssignmentGroupsComputed = groupIds.every((id) => assignmentGroupIds.includes(id));
    if (dilutionFactors.length === 0 || !areAssignmentGroupsComputed) {
      return well as DisplayPlateMapWell;
    }
    const displayInfo: WellDisplayInfo = {
      colorInfo: {type: 'solid', colors: groupIds.map((groupId) => assignmentGroupsById[groupId].color)},
      label: null,
    };
    return {...well, displayInfo};
  });
}

/**
 * Gets display wells for all wells in the given plate map with well role display info.
 * If the well has no well role info, the display well is null.
 * If all wells of one role/subrole are in the same group, do not show any label on the well.
 * Otherwise, label with the group number or symbol.
 */
function getWellRoleDisplayWells(plateMap: PlateMap): Array<DisplayPlateMapWell> {
  const groupsByType = getRolesByRoleType(compact(plateMap.wells.map((well) => well.role)));
  const displayWells: Array<DisplayPlateMapWell> = plateMap.wells.map((well) => {
    const {role} = well;
    let displayInfo: WellDisplayInfo | null = {
      colorInfo: {type: 'solid', colors: []},
      label: null,
    };
    if (role == null) {
      return {...well, displayInfo: null};
    }
    const subroleKey = role.subrole ?? NULL_SUBROLE_KEY;
    const subroleGroups = groupsByType[role.primaryRole]?.[subroleKey] ?? [];
    if (subroleGroups.length <= 1) {
      displayInfo = {...displayInfo, label: getRoleSymbol({...role, group: null})};
    } else {
      displayInfo = {...displayInfo, label: getRoleSymbol(role)};
    }
    return {...well, displayInfo};
  });
  return displayWells;
}

/**
 * Gets display wells for all wells in the given plate map with display info for well volume
 */
function getWellVolumeDisplayWells(plateMap: PlateMap): Array<DisplayPlateMapWell> {
  const wellVolumeValues: Array<number> = plateMap.wells
    .map((well) => well.quantity?.value)
    .filter((value) => value != null);

  const minMax = getMinMaxInfo(wellVolumeValues);
  if (minMax == null) {
    return plateMap.wells as Array<DisplayPlateMapWell>;
  }

  const {minValue, maxValue} = minMax;

  return plateMap.wells.map((well) => {
    const volumeValue = well.quantity?.value;

    if (volumeValue == null) {
      return well as DisplayPlateMapWell;
    }

    const opacity = calculateGradientOpacity(volumeValue, minValue, maxValue);
    const displayValue = volumeValue.toString();
    const label = getNumericDisplayLabel(displayValue, plateMap.size.numRows);

    const displayInfo: WellDisplayInfo = {
      colorInfo: {type: 'solid', colors: [WELL_VOLUME_WELL_COLOR]},
      label,
      opacity,
      error: null,
    };
    return {...well, displayInfo};
  });
}

/**
 * Gets the display attributes to render the given display layer for a plate map.
 */
export function getDisplayPlateMap<
  K extends keyof AssignmentGroupValueTypeByLayer,
  V extends AssignmentGroupValueTypeByLayer[K],
>(
  params: PlateMapDisplayParams<K, V>,
  previewedWellIndices: Set<number>,
  previewedSourceErrorsBySourceDilutionId: SourceErrorsBySourceDilutionId,
  plateMapType?: PlateMapType | null
): DisplayPlateMap {
  const {displayLayer, plateMap, assignmentGroupsById, attribute, groupedValidationErrors} = params;
  if (displayLayer === PlateMapWellDisplayLayer.REAGENT) {
    const displayWells = getReagentDisplayWells({
      plateMap,
      assignmentGroupsById: assignmentGroupsById as {[id: string]: ReagentAssignmentGroup},
      errors:
        groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(attribute)]?.[PlateMapErrorLevel.ERROR] ??
        [],
      previewedSourceErrorsBySourceDilutionId,
      previewedWellIndices,
      plateMapType,
    });
    return {...plateMap, wells: displayWells};
  } else if (displayLayer === PlateMapWellDisplayLayer.WELL_ROLE) {
    const displayWells = getWellRoleDisplayWells(plateMap);
    return {size: plateMap.size, wells: displayWells};
  } else if (displayLayer === PlateMapWellDisplayLayer.WELL_VOLUME) {
    const displayWells = getWellVolumeDisplayWells(plateMap);
    return {size: plateMap.size, wells: displayWells};
  } else if (displayLayer === PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR) {
    const displayWells = getTestDilutionFactorDisplayWells(
      plateMap,
      assignmentGroupsById as {
        [id: string]: AssignmentGroup<
          AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR]
        >;
      }
    );
    return {size: plateMap.size, wells: displayWells};
  } else if (displayLayer === PlateMapWellDisplayLayer.CONCENTRATION) {
    const concentrationAttribute = attribute as ConcentrationAttribute;
    const errors =
      groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(attribute)]?.[PlateMapErrorLevel.ERROR] ??
      [];
    const displayWells = getConcentrationDisplayWells(plateMap, concentrationAttribute, errors);
    return {size: plateMap.size, wells: displayWells};
  } else if (displayLayer === PlateMapWellDisplayLayer.MEASUREMENT) {
    const measurementAttribute = attribute as MeasurementAttribute;
    const displayWells = getMeasurementDisplayWells(plateMap, measurementAttribute);
    return {size: plateMap.size, wells: displayWells};
  }
  // Cast as SchematizedAttribute. Since it is not one of the special layers in the if cases above,
  // we know that we are working with a schema field layer.
  const attributeAsSchematized = attribute as SchematizedAttribute;
  const errors =
    groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(attribute)]?.[PlateMapErrorLevel.ERROR] ?? [];
  switch (displayLayer) {
    case PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT: {
      const displayWells = getTextSchemaTagDisplayWells(
        plateMap,
        assignmentGroupsById as {
          [id: string]: AssignmentGroup<
            AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]
          >;
        },
        attributeAsSchematized,
        errors
      );
      return {size: plateMap.size, wells: displayWells};
    }
    case PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR: {
      const displayWells = getSelectorSchemaTagDisplayWells(
        plateMap,
        assignmentGroupsById as {
          [id: string]: AssignmentGroup<
            AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]
          >;
        },
        attributeAsSchematized,
        errors
      );
      return {size: plateMap.size, wells: displayWells};
    }
    case PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER: {
      const displayWells = getNumberSchemaTagDisplayWells(plateMap, attributeAsSchematized, errors);
      return {size: plateMap.size, wells: displayWells};
    }
    default: {
      return {size: plateMap.size, wells: plateMap.wells as Array<DisplayPlateMapWell>};
    }
  }
}

export interface WellPositionWithGroup {
  position: WellPosition;
  group: number;
}

/**
 * Assign groups to a set of well positions based on the given pattern assignment parameters.
 *
 * Expected behavior:
 * - When fill direction is right, start from the top most cell (on the left)
 * - When fill direction is down, start from the left most cell (at the top)
 * - Move in the replicate direction until:
 *       you finish a replicate
 *       you finish a row
 * - Then start a new replicate (group number) and move in the fill
 * direction.
 */
export function assignGroupsByPattern(
  wellPositions: Array<WellPosition>,
  params: PatternAssignmentParameters
): Array<WellPositionWithGroup> {
  // Nothing to do here.
  if (wellPositions.length === 0) {
    return [];
  }

  // First, sort all positions by row and then column within each row.
  // This guarantees below that the first position in each group will always be the top-left most position.
  const sortedPositions = wellPositions.sort(
    (a: WellPosition, b: WellPosition) => a.row - b.row || a.column - b.column
  );

  // Group all positions into rows or columns, depending on the replicate direction.
  const byFillDirection = groupBy(sortedPositions, (wellPosition) =>
    params.replicateDirection === PatternDirection.RIGHT ? wellPosition.row : wellPosition.column
  );

  // Chunk each row or column into chunks based on the number of replicates we want for each group.
  const wellPositionGroups = Object.values(byFillDirection).flatMap((columnOrRow) => [
    ...chunk(columnOrRow, params.replicates),
  ]);

  /**
   * Sort function for comparing two groups of well positions based on the first well position and fill direction.
   */
  function compareGroups(a: Array<WellPosition>, b: Array<WellPosition>): number {
    const posA = a[0];
    const posB = b[0];
    const rowSort = posA.row - posB.row;
    const columnSort = posA.column - posB.column;
    return params.fillDirection === PatternDirection.RIGHT ? rowSort || columnSort : columnSort || rowSort;
  }

  // Sort the groups based on the fill direction and assign a group number to each group based on its sorted
  // index
  return wellPositionGroups.sort(compareGroups).flatMap((wellPositionGroup, groupIndex) =>
    wellPositionGroup.map((position) => ({
      position,
      group: groupIndex + params.startingGroup,
    }))
  );
}

/**
 * Returns a copy of the given plate map with the given edits applied to the wells.
 */
export function getPlateMapWithWellEdits(
  plateMap: PlateMap,
  wellEdits: {[index: number]: Partial<PlateMapWell>}
): PlateMap {
  return produce(plateMap, (draftPlateMap) => {
    Object.entries(wellEdits).forEach(([index, edits]) => {
      const indexAsNumber = parseInt(index, 10);
      draftPlateMap.wells[indexAsNumber] = {...plateMap.wells[indexAsNumber], ...edits};
    });
  });
}

/**
 * Returns a copy of the given plate map with the given edits applied to all wells.
 */
export function applyEditsToAllWells(plateMap: PlateMap, edits: Partial<PlateMapWell>): PlateMap {
  return produce(plateMap, (draft) => {
    for (const [index, well] of draft.wells.entries()) {
      draft.wells[index] = {
        ...well,
        ...edits,
      };
    }
  });
}

/**
 * Get the role group numbers of the selected wells on the given plate map, sorted numerically.
 * Assumes that all the selected wells will have roles assigned.
 */
export function getSelectedRoleGroups(plateMap: PlateMap, selectedWellIndices: Set<number>): Array<number> {
  const dedupedGroups = new Set([...selectedWellIndices].map((idx) => plateMap.wells[idx].role!.group));
  return sortedBy([...dedupedGroups], [(group) => group]);
}

/**
 * Gets a plate map with the same attribute edits applied to all the given wells.
 */
export function applyEditToSelected(
  plateMap: PlateMap,
  edit: Partial<PlateMapWell>,
  selectedWellIndices: Set<number>
): PlateMap {
  const edits = objectFromEntries([...selectedWellIndices].map((index) => [index, edit]));
  return getPlateMapWithWellEdits(plateMap, edits);
}

/**
 * Set a schema tag on a draft well for a given schematized plate map attribute.
 */
export function writeSchemaTagToDraftWell(
  draftWell: Draft<PlateMapWell>,
  schematizedAttribute: SchematizedAttribute,
  edit: Partial<MinimalSchemaTag>
): void {
  const currentSchemaTag = getSchematizedAttributeTagFromWell(draftWell, schematizedAttribute);
  const newSchemaTag = {
    name: schematizedAttribute.schemaField.name,
    tagSchemaField: schematizedAttribute.schemaField,
    ...currentSchemaTag,
    ...edit,
  };
  if (schematizedAttribute.type === SchematizedAttributeType.WELL_SCHEMA_FIELD) {
    draftWell.schemaTags[schematizedAttribute.key] = newSchemaTag;
  } else if (schematizedAttribute.type === SchematizedAttributeType.DATA_PARAMETER) {
    draftWell.dataParameterTags[schematizedAttribute.key] = newSchemaTag;
  } else {
    assert(false, 'Invalid schematized attribute type');
  }
}

/**
 * Apply the same attribute edits to the schema tag of the given schema field to every selected well.
 */
export function applySchemaTagEditToSelected(
  plateMap: PlateMap,
  schematizedAttribute: SchematizedAttribute,
  edit: Partial<MinimalSchemaTag>,
  selectedWellIndices: Set<number>
): PlateMap {
  return produce(plateMap, (draftPlateMap) => {
    [...selectedWellIndices].forEach((index) => {
      writeSchemaTagToDraftWell(draftPlateMap.wells[index], schematizedAttribute, edit);
    });
  });
}

/**
 * Apply the same attribute edits to the schema tag of the given schema field to every well.
 */
export function applySchemaTagEditToAllWells(
  plateMap: PlateMap,
  schematizedAttribute: SchematizedAttribute,
  edit: Partial<MinimalSchemaTag>
): PlateMap {
  return produce(plateMap, (draft) => {
    draft.wells.forEach((well) => {
      writeSchemaTagToDraftWell(well, schematizedAttribute, edit);
    });
  });
}

/**
 * Gets a plate map with the changes that would be made to the selected wells for the given pattern assignment.
 */
export function getPlateMapWithPatternPreview(
  plateMap: PlateMap,
  groupToWellEdit: (group: number, index: number) => Partial<PlateMapWell>,
  params: PatternAssignmentParameters,
  selectedWellIndices: Set<number>
): PlateMap {
  const selectedPositions = Array.from(selectedWellIndices).map((index) =>
    getWellPositionFromIndex(index, plateMap.size)
  );
  const assignedPositions = assignGroupsByPattern(selectedPositions, params);
  const edits: {[index: number]: Partial<PlateMapWell>} = [];
  for (const {position, group} of assignedPositions) {
    const flattenedIndex = getFlattenedWellIndex(position.row, position.column, plateMap.size);
    edits[flattenedIndex] = groupToWellEdit(group, flattenedIndex);
  }
  return getPlateMapWithWellEdits(plateMap, edits);
}

/**
 * Gets a plate map with concentration values applied to the selected wells for preview.
 */
export function getPlateMapWithConcentrationPreview(
  plateMap: PlateMap,
  concentrationsByWell: {[wellIndex: number]: Measurement},
  layerId: string
): PlateMap {
  const edits: {[index: number]: Partial<PlateMapWell>} = {};
  for (const [wellIndex, concentration] of Object.entries(concentrationsByWell)) {
    const index = parseInt(wellIndex, 10);
    edits[index] = {
      concentrationLayers: {
        ...plateMap.wells[index].concentrationLayers,
        [layerId]: concentration,
      },
    };
  }
  return getPlateMapWithWellEdits(plateMap, edits);
}

/**
 * Gets legend items for reagents on the given plate map. Each reagent grouping is its own legend item.
 */
function getLegendItemsForReagent(
  plateMap: PlateMap,
  assignmentGroupsById: {[id: string]: ReagentAssignmentGroup}
): Array<PlateMapLegendItem> {
  const reagentGroupCounts: {[id: string]: number} = {};
  for (const well of plateMap.wells) {
    const allReagentGroupIds = getGroupIdsForReagents(well.sources, well.contents);
    for (const reagentGroupId of allReagentGroupIds) {
      reagentGroupCounts[reagentGroupId] ??= 0;
      reagentGroupCounts[reagentGroupId]++;
    }
  }

  return Object.values(assignmentGroupsById)
    .filter(
      (assignmentGroup) =>
        reagentGroupCounts[assignmentGroup.id] != null && reagentGroupCounts[assignmentGroup.id] > 0
    )
    .map((assignmentGroup) => ({
      label: assignmentGroup.name,
      icon: (
        <MulticolorStripedCircle
          cx={PLATE_MAP_LEGEND_ICON_SIZE}
          cy={PLATE_MAP_LEGEND_ICON_SIZE}
          radius={PLATE_MAP_LEGEND_ICON_SIZE}
          colors={[assignmentGroup.color]}
        />
      ),
      count: reagentGroupCounts[assignmentGroup.id],
    }));
}

/**
 * Gets legend items for well roles on the given plate map, not including any roles with no groups.
 * Each role and subrole combination is its own legend item.
 */
function getLegendItemsForWellRole(plateMap: PlateMap): Array<PlateMapLegendItem> {
  const rolesByType = getRolesByRoleType(compact(plateMap.wells.map((well) => well.role)));
  const items: Array<PlateMapLegendItem> = [];
  for (const experimentalRoleType of ORDERED_PRIMARY_EXPERIMENTAL_ROLES) {
    const subRoleMap = rolesByType[experimentalRoleType];
    // Don't show an option if there are no wells of that role type
    if (subRoleMap == null) {
      continue;
    }
    for (const [subroleKey, groups] of Object.entries(subRoleMap)) {
      const count = groups.length;
      if (count === 0) {
        continue;
      }

      const legendRole = {
        primaryRole: experimentalRoleType,
        subrole: subroleKey === NULL_SUBROLE_KEY ? null : subroleKey,
        group: null,
      };
      const displaySymbol = getRoleSymbol(legendRole);
      const label = getRoleLabelText(legendRole);
      items.push({
        label,
        icon: (
          <PrimaryExperimentalRoleSymbol
            radius={PLATE_MAP_LEGEND_ICON_SIZE}
            primaryRole={experimentalRoleType}
            label={displaySymbol}
          />
        ),
        count,
      });
    }
  }
  return items;
}

/**
 * Gets legend items for dilution factors on the given plate map.
 * Each dilution factor is its own legend item; note that this grouping is by dilution factor & not test
 * order id, so multiple test orders with the same dilution will not be distinguished.
 */
function getLegendItemsForTestDilutionFactors(
  plateMap: PlateMap,
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR]
    >;
  }
): Array<PlateMapLegendItem> {
  const allDilutionFactors: Array<TestDilutionFactor> = plateMap.wells.flatMap((well) => [
    ...getTestDilutionFactorsForWell(well, plateMap.testOrderDilutionInfoById),
  ]);
  const dilutionFactorCounts = _.countBy(allDilutionFactors, (factor) =>
    getTestDilutionAssignmentGroupId(factor)
  );
  const allLegendItems = Object.values(assignmentGroupsById).map((assignmentGroup) => ({
    label: assignmentGroup.name,
    icon: (
      <MulticolorStripedCircle
        radius={PLATE_MAP_LEGEND_ICON_SIZE}
        cx={PLATE_MAP_LEGEND_ICON_SIZE}
        cy={PLATE_MAP_LEGEND_ICON_SIZE}
        colors={[assignmentGroup.color]}
      />
    ),
    count: dilutionFactorCounts[assignmentGroup.id] ?? 0,
  }));
  return allLegendItems.filter(({count}) => count > 0);
}

/**
 * Gets legend items for text schema field values on the given plate map.
 * Each text value and URL combination is its own legend item.
 */
function getLegendItemsForTextSchemaFields(
  plateMap: PlateMap,
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]
    >;
  },
  attribute: SchematizedAttribute,
  errors: Array<PlateMapAnnotationErrorWithUUID>
): Array<PlateMapLegendItem> {
  const invalidValues = getInvalidFieldValuesFromErrors(errors);
  const tagValueCounts = _.countBy(plateMap.wells, (well) => {
    const schemaTag = getSchematizedAttributeTagFromWell(well, attribute);
    return getValueUrlId(schemaTag?.value, schemaTag?.url);
  });

  return Object.values(assignmentGroupsById)
    .filter(
      (assignmentGroup) =>
        tagValueCounts[assignmentGroup.id] != null && tagValueCounts[assignmentGroup.id] > 0
    )
    .map((assignmentGroup) => ({
      label: assignmentGroup.name,
      icon: (
        <MulticolorStripedCircle
          radius={PLATE_MAP_LEGEND_ICON_SIZE}
          cx={PLATE_MAP_LEGEND_ICON_SIZE}
          cy={PLATE_MAP_LEGEND_ICON_SIZE}
          colors={[assignmentGroup.color]}
        />
      ),
      count: tagValueCounts[assignmentGroup.id],
      error: invalidValues.has(assignmentGroup.id),
    }));
}

/**
 * Gets legend items for selector schema field values on the given plate map.
 * Only includes options that are assigned to at least one well
 */
function getLegendItemsForSelectorSchemaFields(
  plateMap: PlateMap,
  assignmentGroupsById: {
    [id: string]: AssignmentGroup<
      AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]
    >;
  },
  attribute: SchematizedAttribute,
  errors: Array<PlateMapAnnotationErrorWithUUID>
): Array<PlateMapLegendItem> {
  const invalidValues = getInvalidFieldValuesFromErrors(errors);
  const allSelectedOptionIds = plateMap.wells.flatMap(
    (well) => getSchematizedAttributeTagFromWell(well, attribute)?.schemaFieldSelectorOptionIds ?? []
  );
  const tagValueCounts = _.countBy(allSelectedOptionIds, (id) => id);
  const allLegendItems = Object.values(assignmentGroupsById).map((assignmentGroup) => ({
    label: assignmentGroup.name,
    icon: (
      <MulticolorStripedCircle
        radius={PLATE_MAP_LEGEND_ICON_SIZE}
        cx={PLATE_MAP_LEGEND_ICON_SIZE}
        cy={PLATE_MAP_LEGEND_ICON_SIZE}
        colors={[assignmentGroup.color]}
      />
    ),
    count: tagValueCounts[assignmentGroup.id] ?? 0,
    error: invalidValues.has(assignmentGroup.id),
  }));
  return allLegendItems.filter(({count}) => count > 0);
}

/**
 * Gets legend items for integer and float values on the given plate map,
 * where there are only two legend items representing the minimum and maximum values applied.
 */
function getLegendItemsForNumberAttribute(
  plateMap: PlateMap,
  attribute: PlateMapDisplayLayerAttribute,
  displayLayer?: PlateMapWellDisplayLayer
): Array<PlateMapLegendItem> {
  const minMax = getDisplayMinMaxForNumberAttribute(plateMap, attribute);
  if (minMax == null) {
    return [];
  }
  const unitString = getUnitStringForAttribute(plateMap, attribute, displayLayer!);
  const minValueLabel = unitString ? `${minMax.minValue} ${unitString}` : `${minMax.minValue}`;
  const maxValueLabel = unitString ? `${minMax.maxValue} ${unitString}` : `${minMax.maxValue}`;
  return [
    {label: minValueLabel, icon: null, count: 0},
    {label: maxValueLabel, icon: null, count: 0},
  ];
}

/**
 * Gets items to show in the plate map legend for the given plate map and display layer.
 */
export function getLegendItems<
  K extends keyof AssignmentGroupValueTypeByLayer,
  V extends AssignmentGroupValueTypeByLayer[K],
>(params: PlateMapDisplayParams<K, V>): Array<PlateMapLegendItem> {
  const {displayLayer, plateMap, assignmentGroupsById, attribute, groupedValidationErrors} = params;
  const errors =
    groupedValidationErrors[getErrorKeyForDisplayLayerAttribute(attribute)]?.[PlateMapErrorLevel.ERROR] ?? [];
  switch (displayLayer) {
    case PlateMapWellDisplayLayer.REAGENT:
      return getLegendItemsForReagent(
        plateMap,
        assignmentGroupsById as {[id: string]: ReagentAssignmentGroup}
      );
    case PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR:
      return getLegendItemsForTestDilutionFactors(
        plateMap,
        assignmentGroupsById as {
          [id: string]: AssignmentGroup<
            AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR]
          >;
        }
      );
    case PlateMapWellDisplayLayer.WELL_ROLE:
      return getLegendItemsForWellRole(plateMap);
    case PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT: {
      return getLegendItemsForTextSchemaFields(
        plateMap,
        assignmentGroupsById as {
          [id: string]: AssignmentGroup<
            AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT]
          >;
        },
        attribute as SchematizedAttribute,
        errors
      );
    }
    case PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR: {
      return getLegendItemsForSelectorSchemaFields(
        plateMap,
        assignmentGroupsById as {
          [id: string]: AssignmentGroup<
            AssignmentGroupValueTypeByLayer[PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR]
          >;
        },
        attribute as SchematizedAttribute,
        errors
      );
    }
    case PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER:
    case PlateMapWellDisplayLayer.CONCENTRATION:
    case PlateMapWellDisplayLayer.MEASUREMENT:
    case PlateMapWellDisplayLayer.WELL_VOLUME:
      return getLegendItemsForNumberAttribute(plateMap, attribute, displayLayer);
    default:
      return [];
  }
}

/**
 * Gets the string key for the given attribute, which is the attribute itself if it is a sentinel value,
 * or the unique schema field system name.
 */
export function getDisplayLayerAttributeKey(attribute: PlateMapDisplayLayerAttribute): string {
  return typeof attribute === 'string' ? attribute : `${attribute.type}-${attribute.key}`;
}

const FIELD_TYPE_TO_DISPLAY_LAYER: {[key: string]: PlateMapWellDisplayLayer} = {
  [FieldType.TEXT]: PlateMapWellDisplayLayer.SCHEMA_FIELD_TEXT,
  [FieldType.INTEGER]: PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER,
  [FieldType.FLOAT]: PlateMapWellDisplayLayer.SCHEMA_FIELD_NUMBER,
  [FieldType.SELECTOR]: PlateMapWellDisplayLayer.SCHEMA_FIELD_SELECTOR,
};

/**
 * Type guard to check if an attribute is a SchematizedAttribute
 */
export function isSchematizedAttribute(
  attribute: PlateMapDisplayLayerAttribute
): attribute is SchematizedAttribute {
  return (
    typeof attribute === 'object' &&
    attribute != null &&
    (attribute as SchematizedAttribute).schemaField != null &&
    (attribute as SchematizedAttribute).plannedValues != null
  );
}

/**
 * Gets the corresponding display layer to use for a selected attribute based on the field type
 * of the schema if the attribute is a schema field, or for the given sentinel value.
 */
export function getDisplayLayerForAttribute(
  attribute: PlateMapDisplayLayerAttribute
): PlateMapWellDisplayLayer {
  if (attribute === WELL_ROLE_ATTRIBUTE_SENTINEL) {
    return PlateMapWellDisplayLayer.WELL_ROLE;
  } else if (attribute === REAGENTS_ATTRIBUTE_SENTINEL) {
    return PlateMapWellDisplayLayer.REAGENT;
  } else if (attribute === WELL_VOLUME_ATTRIBUTE_SENTINEL) {
    return PlateMapWellDisplayLayer.WELL_VOLUME;
  } else if (attribute === TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL) {
    return PlateMapWellDisplayLayer.TEST_DILUTION_FACTOR;
  } else if (attribute.type === CONCENTRATION_ATTRIBUTE_SENTINEL) {
    return PlateMapWellDisplayLayer.CONCENTRATION;
  } else if (
    attribute.type === PlateMapMeasurementType.FINAL_CONCENTRATION ||
    attribute.type === PlateMapMeasurementType.TRANSFER_QUANTITY ||
    attribute.type === PlateMapMeasurementType.CONTENT_CONCENTRATION
  ) {
    return PlateMapWellDisplayLayer.MEASUREMENT;
  } else if (isSchematizedAttribute(attribute)) {
    return FIELD_TYPE_TO_DISPLAY_LAYER[attribute.schemaField.fieldType];
  }
  throw new Error(`Invalid attribute type: ${attribute.type}`);
}

interface ReagentSchemaNode extends AccessibleGraphQLObject {
  id: string;
  iconId: string;
  name: string;
}

function makeReagentGroupsById(
  colorGenerator: Generator<string, string>,
  schemasById: Record<string, ReagentSchemaNode>,
  includeInaccessibleGroup: boolean
): {[id: string]: ReagentAssignmentGroup} {
  const reagentAssignmentGroups = [];
  for (const [id, schemaNode] of Object.entries(schemasById)) {
    reagentAssignmentGroups.push({
      id,
      name: schemaNode.name,
      color: colorGenerator.next().value,
      value: {id, iconId: schemaNode.iconId as BenchlingIconName},
    });
  }
  if (includeInaccessibleGroup) {
    reagentAssignmentGroups.push(INACCESSIBLE_ASSIGNMENT_GROUP);
  }
  return Object.fromEntries(reagentAssignmentGroups.map((group) => [group.id, group]));
}

/**
 * For the given attribute, group the values for that attribute on the given plate map into AssignmentGroups
 * with distinct colors and ID value defining the group, and return a mapping of those groups by ID.
 */
export async function createAssignmentGroupsForAttribute(
  plateMap: PlateMap,
  attribute: PlateMapDisplayLayerAttribute
): Promise<{
  [id: string]: AssignmentGroup<AssignmentGroupValue>;
}> {
  const colorGenerator = getColor({});
  if (attribute === REAGENTS_ATTRIBUTE_SENTINEL) {
    const dedupedTagSchemaIds = new Set<string>();
    const dedupedContainerSchemaIds = new Set<string>();
    for (const well of plateMap.wells) {
      for (const content of well.contents) {
        dedupedTagSchemaIds.add(content.groupId);
      }
      for (const source of well.sources) {
        if (source.type === ReagentType.SOURCE_CONTAINABLE) {
          dedupedTagSchemaIds.add(source.groupId);
        } else if (source.contents.length === 0) {
          dedupedContainerSchemaIds.add(source.groupId);
        } else {
          for (const sourceContent of source.contents) {
            dedupedTagSchemaIds.add(sourceContent.groupId);
          }
        }
      }
    }
    // Don't bother fetching assignment groups for inaccessible contents.
    const tagSchemaIds = [...dedupedTagSchemaIds];
    const [accessibleTagSchemaIds, inaccessibleTagSchemaIds] = partition(
      tagSchemaIds,
      (tagSchemaId) => tagSchemaId !== INACCESSIBLE_GROUP_ID_SENTINEL
    );
    const containerSchemaIds = [...dedupedContainerSchemaIds].filter(
      (groupId) => groupId !== INACCESSIBLE_GROUP_ID_SENTINEL
    );
    const [accessibleContainerSchemaIds, inaccessibleContainerSchemaIds] = partition(
      containerSchemaIds,
      (containerSchemaId) => containerSchemaId !== INACCESSIBLE_GROUP_ID_SENTINEL
    );
    const schemasById = await fetchReagentSchemasById(accessibleContainerSchemaIds, accessibleTagSchemaIds);
    return makeReagentGroupsById(
      colorGenerator,
      schemasById,
      inaccessibleTagSchemaIds.length > 0 || inaccessibleContainerSchemaIds.length > 0
    );
  } else if (attribute === WELL_ROLE_ATTRIBUTE_SENTINEL) {
    return {};
  } else if (attribute === WELL_VOLUME_ATTRIBUTE_SENTINEL) {
    return {};
  } else if (attribute === TEST_DILUTION_FACTOR_ATTRIBUTE_SENTINEL) {
    // Create a group for each dilution factor currently used in the plate map
    // Note that we group by dilution factor & not test order id, so multiple test orders may be grouped
    // together
    const allDilutionFactors: Array<TestDilutionFactor> = sortedBy(
      plateMap.wells.flatMap((well) => [
        ...getTestDilutionFactorsForWell(well, plateMap.testOrderDilutionInfoById),
      ]),
      [(dilutionFactor) => dilutionFactor]
    );
    const assignmentGroupsById: {
      [id: string]: AssignmentGroup<AssignmentGroupValue>;
    } = {};
    for (const dilutionFactor of allDilutionFactors) {
      const dilutionFactorGroupId = getTestDilutionAssignmentGroupId(dilutionFactor);
      if (assignmentGroupsById[dilutionFactorGroupId] == null) {
        assignmentGroupsById[dilutionFactorGroupId] = {
          id: dilutionFactorGroupId,
          color:
            dilutionFactor === NULL_TEST_DILUTION_FACTOR_SENTINEL
              ? NULL_TEST_DILUTION_FACTOR_COLOR
              : colorGenerator.next().value,
          name: testDilutionFactorToDisplay(dilutionFactor),
          value: dilutionFactor,
        };
      }
    }
    return assignmentGroupsById;
  } else if (
    attribute.type === CONCENTRATION_ATTRIBUTE_SENTINEL ||
    attribute.type === PlateMapMeasurementType.FINAL_CONCENTRATION ||
    attribute.type === PlateMapMeasurementType.TRANSFER_QUANTITY ||
    attribute.type === PlateMapMeasurementType.CONTENT_CONCENTRATION
  ) {
    return {};
  } else if (isSchematizedAttribute(attribute) && attribute.schemaField.fieldType === FieldType.TEXT) {
    // Create a group for each unique text and url combination currently used on the plate map
    const allNonEmptyValues = plateMap.wells
      .map((well) => getSchematizedAttributeTagFromWell(well, attribute))
      .filter((schemaTag): schemaTag is MinimalSchemaTag => Boolean(schemaTag?.value))
      .map((schemaTag) => ({
        text: schemaTag.value!,
        url: schemaTag.url ?? '',
      }));
    const assignmentGroupsById: {
      [id: string]: AssignmentGroup<AssignmentGroupValue>;
    } = {};
    const plannedValues = attribute.plannedValues
      .filter((plannedValue) => plannedValue.value != null)
      .map((plannedValue) => ({
        text: plannedValue.value!,
        url: plannedValue.url ?? '',
      }));
    for (const value of [...plannedValues, ...allNonEmptyValues]) {
      const id = getValueUrlId(value.text, value.url)!;
      if (assignmentGroupsById[id] == null) {
        assignmentGroupsById[id] = {
          id,
          color: colorGenerator.next().value,
          name: value.text,
          value,
        };
      }
    }
    return assignmentGroupsById;
  } else if (isSchematizedAttribute(attribute) && attribute.schemaField.fieldType === FieldType.SELECTOR) {
    // Create a group for each dropdown selector option
    if (isSchemaFieldDatabaseBacked(attribute.schemaField)) {
      const selectorOptions = await fetchDropdownOptionsForSelector(
        attribute.schemaField.schemaFieldSelectorId!
      );
      const groupEntries: Array<[string, AssignmentGroup<string>]> = selectorOptions.map((option) => [
        option.id,
        {
          id: option.id,
          color: colorGenerator.next().value,
          name: option.name,
          value: option.id,
        },
      ]);
      return objectFromEntries(groupEntries);
    } else {
      return getSelectorAssignmentGroupsFromPlate(plateMap, attribute, colorGenerator);
    }
  } else {
    return {};
  }
}

/**
 * Construct attribute groups from all the values of a selector schema field on a plate map. This is used for
 * read only plate maps; if we need to edit the values, we instead fetch all possible selector options.
 */
export function getSelectorAssignmentGroupsFromPlate(
  plateMap: PlateMap,
  attribute: SchematizedAttribute,
  colorGenerator: Generator<string, string>
): {
  [id: string]: AssignmentGroup<AssignmentGroupValue>;
} {
  const assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>} = {};
  const allNonEmptyValues = plateMap.wells
    .map((well) => getSchematizedAttributeTagFromWell(well, attribute))
    .filter((schemaTag): schemaTag is MinimalSchemaTag => Boolean(schemaTag?.value));
  // The display value for multi-select fields is a comma separated list of the display values.
  // TODO(BNCH-97442): Get the names in a more sensible way.
  allNonEmptyValues.forEach((schemaTag) => {
    _.zip(schemaTag.schemaFieldSelectorOptionIds!, schemaTag.value!.split(',')).forEach(([id, name]) => {
      if (assignmentGroupsById[id] == null) {
        assignmentGroupsById[id] = {
          id,
          color: colorGenerator.next().value,
          name: name.trim(),
          value: id,
        };
      }
    });
  });
  return assignmentGroupsById;
}

/**
 * Fetch all the dropdown options for the given dropdown selector with unarchived options listed first.
 */
export async function fetchDropdownOptionsForSelector(
  selectorId: string
): Promise<Array<SchemaSelectorOption>> {
  const {schemaFieldSelectors} = await doQuerySafe<SchemaSelectorsQuery, SchemaSelectorsQueryVariables>(
    {query: SCHEMA_SELECTORS_QUERY},
    {variables: {includeArchived: false}}
  );
  const selector = schemaFieldSelectors.find((selector) => selector.id === selectorId)!;
  return sortedBy(selector.allSchemaFieldSelectorOptions, [
    ({archiveRecord}) => (archiveRecord == null ? 0 : 1),
  ]);
}

async function fetchReagentSchemasById(
  storableSchemaIds: Array<string>,
  tagSchemaIds: Array<string>
): Promise<Record<string, ReagentSchemaNode>> {
  const {storableSchemas, tagSchemaChips} = await doQuerySafe<
    ReagentSchemasQuery,
    ReagentSchemasQueryVariables
  >({query: REAGENT_SCHEMAS_QUERY}, {variables: {storableSchemaIds, tagSchemaIds}});
  const filteredStorableSchemas = accessibleObjs<ReagentSchemaNode>(
    storableSchemas as Array<ReagentQueryContainerSchemaNode>
  );
  const filteredTagSchemas = accessibleObjs<ReagentSchemaNode>(tagSchemaChips);
  return Object.fromEntries(
    [...filteredTagSchemas, ...filteredStorableSchemas].map((schema) => [schema.id, schema])
  );
}

export function flattenSources(well: PlateMapWell): Array<BioentitySource> {
  return well.sources.flatMap((reagent) =>
    reagent.type === ReagentType.SOURCE_CONTAINABLE ? [reagent] : reagent.contents
  );
}

export function wellHasAnySourceContainersWithNoContents(well: PlateMapWell): boolean {
  const containers = well.sources.filter(
    (source): source is ContainerSource => source.type === ReagentType.SOURCE_CONTAINER
  );
  return containers.some(
    (container) =>
      !isSourceContainerValidForTransfer({containedItemIds: container.contents.map((content) => content.id)})
  );
}

export function wellHasTooManySources(well: PlateMapWell): boolean {
  return flattenSources(well).length > PLATE_MAP_MAX_WELL_SOURCES;
}

export async function fetchContainerSchemaFields(schemaId: string): Promise<Array<SchemaField>> {
  const {storableSchema} = await doQuerySafe<ContainerSchemaQuery, ContainerSchemaQueryVariables>(
    {query: CONTAINER_SCHEMA_QUERY},
    {variables: {storableSchemaId: schemaId}}
  );
  if (isInaccessible(storableSchema) || storableSchema.__typename !== 'ContainerSchemaNode') {
    return [];
  }
  return storableSchema.fieldsJson;
}

/**
 * Get a map of data parameter version ids to data parameter names.
 */
export function getDataParameterNamesByVersionId(plateDataParameters: Array<PlateDataParameter>): {
  [dataParamId: string]: string;
} {
  return objectFromEntries(plateDataParameters.map((param) => [param.versionId, param.name]));
}

/**
 * Get a map of data parameter ids to empty schema tags for each data parameter.
 */
export function getInitialDataParameterTags(plateDataParameters: Array<PlateDataParameter>): {
  [id: string]: MinimalSchemaTag;
} {
  return objectFromEntries(
    plateDataParameters.map((dataParam) => [dataParam.versionId, getInitialMinimalTag(dataParam.schemaField)])
  );
}

/**
 * Construct a PlateDataParameter from a frozen parameter value.
 */
export function getStaleDataParameterFromFrozenValue(
  frozenValue: SupportedFrozenDataParameterValue
): PlateDataParameter {
  return {
    // We only make stale parameters for parameters we don't have live values for. If we don't have a live value,
    // then it can't be instep or have any planned values.
    inStep: false,
    // frozen values didn't always freeze the name of the data parameter. In those cases the fieldName was
    // overwritten using the data parameter's name instead.
    name: frozenValue.dataParameterName ?? frozenValue.frozenSchemaFieldValue.frozenMetadata.fieldName,
    plannedValues: [],
    schemaField: {
      name: frozenValue.frozenSchemaFieldValue.frozenMetadata.fieldName,
      fieldType: frozenValue.frozenSchemaFieldValue.frozenMetadata.fieldType,
    },
    type: PlateProcedureParameterType.DATA_PARAMETER,
    versionId: frozenValue.dataParameterVersionId,
    outOfContext: true,
  };
}

/**
 * Checks whether any wells in a plate map have changed the value for a given schema field from what it was
 * before the plate map began to be edited. This is different than checking whether the schema field is set to
 * a non-default value _for the field_, as with the case where a user is annotating or creating a plate record
 * for a plate that already exists and may or may not have been annotated before. Uses react-fast-compare for
 * object deep equality.
 */
export function anyWellsHaveChangedTagForSchemaField(
  plateMap: PlateMap,
  initialPlateMap: PlateMap,
  schemaFieldId: string
): boolean {
  for (const [index, well] of plateMap.wells.entries()) {
    const initialWell = initialPlateMap.wells[index];
    const schemaTag = well.schemaTags[schemaFieldId];
    const initialSchemaTag = initialWell.schemaTags[schemaFieldId];
    if (!isEqual(schemaTag, initialSchemaTag)) {
      return true;
    }
  }
  return false;
}

/**
 * Checks whether any wells in a plate map have changed the value for a given data parameter from what it was
 * before the plate map began to be edited. Uses react-fast-compare for object deep equality.
 */
export function anyWellsHaveChangedTagForParameter(
  plateMap: PlateMap,
  initialPlateMap: PlateMap,
  parameter: PlateDataParameter
): boolean {
  for (const [index, well] of plateMap.wells.entries()) {
    const initialWell = initialPlateMap.wells[index];
    const initialDataParameterTag = initialWell.dataParameterTags[parameter.versionId];
    const dataParameterTag = well.dataParameterTags[parameter.versionId];
    if (!isEqual(dataParameterTag, initialDataParameterTag)) {
      return true;
    }
  }
  return false;
}

/**
 * Determines which of the list of all data parameters in a procedure should be initially selected for
 * annotation on this plate map.
 */
export function getInitialSelectedDataParameters(
  plateDataParameters: Array<PlateDataParameter> = []
): Array<PlateDataParameter> {
  const thisStepParameters = plateDataParameters.filter((parameter) => parameter.inStep);
  if (thisStepParameters.length <= PLATE_MAP_MAX_DATA_PARAMETER_LAYERS) {
    return thisStepParameters;
  } else {
    return [];
  }
}

/**
 * Create an empty plate map representing a new plate for the given plate schema.
 */
export function getEmptyPlateMap(
  plateSchema: PlateSchema,
  containerSchemaFields: Array<SchemaField>,
  plateDataParameters: Array<PlateDataParameter> = []
): PlateMap {
  const initialWellSchemaTags = objectFromEntries(
    getInitialTags(containerSchemaFields).map((tag) => [tag.tagSchemaField.apiId, tag])
  );
  const size = {numRows: plateSchema.height, numColumns: plateSchema.width};
  const wells: Array<PlateMapWell> = [];
  const initialSelectedDataParameters = getInitialSelectedDataParameters(plateDataParameters);
  for (const {row, column} of wellPositionIterator(size)) {
    wells.push({
      position: {row, column},
      role: null,
      sources: [],
      contents: [],
      schemaTags: initialWellSchemaTags,
      dataParameterTags: getInitialDataParameterTags(initialSelectedDataParameters),
      quantity: getDefaultMeasurement(),
      finalQuantity: getDefaultMeasurementWithErrors(),
      concentrationLayers: {},
    });
  }

  return {
    id: null,
    size,
    wells,
    dataParameterNamesByVersionId: getDataParameterNamesByVersionId(plateDataParameters),
    testOrderDilutionInfoById: {},
  };
}

export function isWellEmpty(well: PlateMapWell): boolean {
  return well.contents.length === 0 && well.sources.length === 0;
}

export interface ContainerForValidation extends MakeFieldsRequired<SampleStatusItem, 'permissions'> {
  containedItemIds: Array<string>;
  inaccessibleContentIds: Array<string>;
  name: string;
  parentStorableId?: Optional<string>;
}

/**
 * Return a warning for why a container cannot be used to fill a plate, or null if it can be.
 * @param container Container to validate.
 * @param destinationPlateId ID of the plate being filled.
 * @param validateContents Whether to validate container contents. Some queries don't include
 *                         archived and inaccesible contents so this validation needs to be deferred.
 */
export function validateContainerForFill(
  container: ContainerForValidation,
  destinationPlateId: string | null,
  validateContents: boolean = true
): string | null {
  if (!container.permissions.writable) {
    return 'Insufficient permissions to use this container';
  } else if (
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) &&
    !doesCurrentUserHaveAccessForItem(container)
  ) {
    return `Only ${getRestrictedSampleUsersDisplayText()} can use this container`;
  } else if (!isItemAvailableToCurrentUser(container)) {
    return getCICOWarningText(container.checkOutStatusInfo!, {hideAssignee: false});
  } else if (destinationPlateId != null && container.parentStorableId === destinationPlateId) {
    return 'Cannot fill plate from its own wells';
  } else if (validateContents && !isSourceContainerValidForTransfer(container)) {
    return getTransferInputContentsErrorText(container);
  } else if (validateContents && container.inaccessibleContentIds.length > 0) {
    return 'This container holds inaccessible contents';
  } else if (container.containedItemIds.length > PLATE_MAP_MAX_WELL_SOURCES) {
    return `Source container ${container.name} has ${container.containedItemIds.length} contained items, but only ${PLATE_MAP_MAX_WELL_SOURCES} contained items can be transferred at once.`;
  }
  return null;
}

/**
 * Determines whether the well has been modified.
 * @param well The well to check
 */
export function hasWellBeenModified(well: PlateMapWell): boolean {
  // If the well has sources or its final quantity has been updated, short-circuit.
  if (well.sources.length > 0 || !isEqual(well.quantity, well.finalQuantity.measurement)) {
    return true;
  }
  // Check all the contents to see if their concentrations have been modified.
  return well.contents.some(
    (content) => !isEqual(content.initialConcentration, content.finalConcentration.measurement)
  );
}

/**
 * Given the old offset and the new limit and total for a pagination, determine what the new offset should be.
 */
export function getPaginationOffset(oldOffset: number, limit: number, total: number): number {
  // If we've paged far enough and subsequently lowered total enough that our offset exceeds the current
  // total, bump offset back to the start of the last page.
  const numPages = Math.floor(total / limit);
  let startOfLastPage = 0;
  if (numPages > 0) {
    startOfLastPage = total % limit === 0 ? total - limit : numPages * limit;
  }
  return Math.min(oldOffset, startOfLastPage);
}

/**
 * Given a supported field type, get the relevant icon.
 */
export function getIconFromFieldType(fieldType: SupportedFieldType): BenchlingIconName {
  switch (fieldType) {
    case FieldType.TEXT:
      return 'text';
    case FieldType.FLOAT:
    case FieldType.INTEGER:
      return 'number';
    case FieldType.SELECTOR:
      return 'list';
    case FieldType.BIOENTITY_LINK:
      // TODO(BNCH-94186): Constrain by target schema
      return FolderItemIcon.BasicFolderItem;
    default:
      return 'field';
  }
}

/**
 * For the given container schema query data, filter to the options that are supported on plate maps
 * and transform them to DisplayLayerMetadata to use in display layer selector components.
 */
export function getWellSchemaDisplayLayerOptions(
  containerSchemaData: ContainerSchemaQuery | undefined
): Array<LiveSchematizedDisplayLayerMetadata> {
  // Retrieved schema is expected to be a container schema. Do not show schema field options if not.
  if (
    containerSchemaData == null ||
    isInaccessible(containerSchemaData.storableSchema) ||
    containerSchemaData.storableSchema.__typename !== 'ContainerSchemaNode'
  ) {
    return [];
  }
  const fields: Array<SchemaField> = containerSchemaData.storableSchema.fieldsJson;
  const validFields = fields.filter(
    (field) => SUPPORTED_FIELD_TYPES_FOR_ANNOTATION.has(field.fieldType) && !isUneditableField(field)
  );
  return validFields.map((field) => ({
    attribute: {
      key: field.apiId,
      schemaField: field,
      type: SchematizedAttributeType.WELL_SCHEMA_FIELD,
      plannedValues: [],
    },
    displayName: field.displayName,
    icon: getIconFromFieldType(field.fieldType as SupportedFieldTypeForAnnotation),
  }));
}

/**
 * Transform plate parameters to display layer options.
 */
export function getPlateDataParametersOptions(
  plateDataParameters: Array<PlateDataParameter>
): Array<DisplayLayerMetadata> {
  return plateDataParameters.map((parameter) => ({
    attribute: {
      key: parameter.versionId,
      schemaField: parameter.schemaField,
      type: SchematizedAttributeType.DATA_PARAMETER,
      plannedValues: parameter.plannedValues,
    },
    displayName: parameter.name,
    icon: getIconFromFieldType(parameter.schemaField.fieldType as SupportedFieldTypeForDataParameters),
  }));
}

/**
 * Transform reagent groups into measurement layer options for final concentration display.
 */
export function getReagentFinalConcentrationOptions(reagentGroupsById: {
  [id: string]: ReagentAssignmentGroup;
}): Array<DisplayLayerMetadata> {
  return Object.values(reagentGroupsById).map((reagentGroup) => ({
    attribute: {
      key: reagentGroup.id,
      type: PlateMapMeasurementType.FINAL_CONCENTRATION,
      color: reagentGroup.color,
    },
    displayName: reagentGroup.name,
    icon: 'circle',
  }));
}

/**
 * For each reagent assignment group representing well contents, create a measurement layer option
 * for displaying content concentration.
 */
export function getContentConcentrationOptions(reagentGroupsById: {
  [id: string]: ReagentAssignmentGroup;
}): Array<DisplayLayerMetadata> {
  return Object.values(reagentGroupsById).map((reagentGroup) => ({
    attribute: {
      key: reagentGroup.id,
      type: PlateMapMeasurementType.CONTENT_CONCENTRATION,
      color: reagentGroup.color,
    },
    displayName: reagentGroup.name,
    icon: 'circle',
  }));
}

/**
 * Transform reagent groups into measurement layer options for transfer quantity display.
 */
export function getReagentTransferQuantityOptions(reagentGroupsById: {
  [id: string]: ReagentAssignmentGroup;
}): Array<DisplayLayerMetadata> {
  return Object.values(reagentGroupsById).map((reagentGroup) => ({
    attribute: {
      key: reagentGroup.id,
      type: PlateMapMeasurementType.TRANSFER_QUANTITY,
      color: reagentGroup.color,
    },
    displayName: reagentGroup.name,
    icon: 'circle',
  }));
}

export function getMaterialInputIdsUsedInWell(well: PlateMapWell): Array<string> {
  return well.sources.flatMap((source) => {
    const bioentitiesInSource = source.type === ReagentType.SOURCE_CONTAINER ? source.contents : [source];
    return bioentitiesInSource
      .map((bioentity) => bioentity.materialInput?.materialInputId)
      .filter(
        (matInputId): matInputId is string => matInputId != null && matInputId !== NO_MATERIAL_INPUT_SENTINEL
      );
  });
}

export function plateMapHasAnyMaterialInputs(plateMap: PlateMap): boolean {
  return plateMap.wells.some((well) => getMaterialInputIdsUsedInWell(well).length > 0);
}

/**
 * @deprecated Prefer InventoryUnitUtil.isVolumeOrNullUnit
 */
export function isVolumeOrNullUnits(units: string | null): boolean {
  if (units == null) {
    return true;
  }
  return ['L', 'mL', 'uL', 'nL', 'pL', 'gal (US)', 'qt (US)', 'pt (US)'].includes(units);
}

/**
 * @deprecated Should be replaced with a version that uses InventoryUnitUtil.isVolumeOrNullUnit
 *
 * Returns whether the grid has any cells or contents that use non-volume units.
 */
export function hasSolidUnits(grid: Grid): boolean {
  for (const cellRow of grid.cells) {
    for (const cell of cellRow) {
      // TODO (BNCH-96579): Also should be checking for "solid" concentrations in addition to quantities.
      if (isAccessibleGridCell(cell) && !isVolumeOrNullUnits(cell.container.quantity?.units)) {
        return true;
      }
    }
  }
  return false;
}

// TODO (BNCH-114811): Move this to TagUtil and refactor Schema/Tag types.
export function getInitialMinimalTag(field: SchemaField | MinimalSchemaField): MinimalSchemaTag {
  if (isSchemaFieldDatabaseBacked(field)) {
    return getInitialTag(field);
  }
  const initialTag: MinimalSchemaTag = {
    tagSchemaField: field,
    value: '',
    url: '',
    booleanValue: null,
    datetimeValue: null,
    floatValue: null,
    integerValue: null,
    jsonValue: null,
    schemaFieldSelectorOptionIds: null,
    targetBatches: null,
    targetEntries: null,
    targetFiles: null,
    targetSamples: null,
    targetStorables: null,
  };
  if (field.fieldType === FIELD_TYPES.SELECTOR.type) {
    initialTag.schemaFieldSelectorOptionIds = [];
  }
  const linksKey = getLinksKeyFromFieldType(field.fieldType);
  if (linksKey) {
    initialTag[linksKey] = [];
  }
  return initialTag;
}

export function convertHydratedUnitToMeasurementUnit(hydratedUnit: HydratedUnit): MeasurementUnit {
  return {
    ...hydratedUnit,
    conversionFactor: hydratedUnit.conversionFactor.toString(),
  };
}

/**
 * Calculates concentrations for wells based on active concentration layer properties.
 *
 * @param settings - The active concentration layer with base concentration and dilution settings
 * @param selectedWellIndices - Set of well indices to calculate concentrations for
 * @param plateMapSize - The size of the plate map
 * @returns Map of well index to calculated concentration value
 */
export function calculateWellConcentrations(
  selectedWellIndices: Set<number>,
  plateMapSize: PlateMapSize,
  settings: ConcentrationSettingsWithDilutionSettings
): {[wellIndex: number]: Measurement} {
  if (selectedWellIndices.size === 0) {
    return {};
  }

  if (settings.concentration == null || settings.concentrationUnit == null) {
    return {};
  }

  const concentrationsByWell: {[wellIndex: number]: Measurement} = {};

  const {dilutionFactor, dilutionDirection, replicates, replicateDirection} = settings.dilutionSettings;

  if (
    !settings.allowSetDilutionSettings ||
    dilutionFactor == null ||
    dilutionFactor <= 0 ||
    replicates == null
  ) {
    // Simple concentration assignment - all selected wells get the same concentration
    for (const wellIndex of selectedWellIndices) {
      concentrationsByWell[wellIndex] = {
        value: settings.concentration,
        unit: convertHydratedUnitToMeasurementUnit(settings.concentrationUnit),
      };
    }
  } else {
    // Concentration assignment with dilution settings
    const patternParams: PatternAssignmentParameters = {
      fillDirection: dilutionDirection,
      replicateDirection,
      replicates,
      startingGroup: 1,
    };
    const selectedWellPositions: Array<WellPosition> = Array.from(selectedWellIndices).map((wellIndex) =>
      getWellPositionFromIndex(wellIndex, plateMapSize)
    );
    const wellGroups = assignGroupsByPattern(selectedWellPositions, patternParams);

    // Calculate concentrations for each group based on dilution factor and replicates
    for (const {position, group} of wellGroups) {
      const wellIndex = getFlattenedWellIndex(position.row, position.column, plateMapSize);

      // Calculate diluted concentration: starting concentration / (dilution factor ^ (group - 1))
      const dilutionMultiple = Math.pow(dilutionFactor, group - 1);
      const rawConcentration = settings.concentration / dilutionMultiple;
      const dilutedConcentration = parseFloat(rawConcentration.toPrecision(getMaxSignificantFigures()));

      concentrationsByWell[wellIndex] = {
        value: dilutedConcentration,
        unit: convertHydratedUnitToMeasurementUnit(settings.concentrationUnit),
      };
    }
  }

  return concentrationsByWell;
}

/**
 * Gets all existing well concentrations that have the given concentration layer and returns
 * them with the unit updated to the new unit. This does not modify values, only units.
 *
 * @returns Map of well indices to measurements with updated units
 */
export function getExistingWellConcentrationsWithNewUnit(
  plateMap: PlateMap,
  concentrationLayerId: string,
  newUnit: HydratedUnit | null
): {[wellIndex: number]: Measurement} {
  if (newUnit == null) {
    return {};
  }
  const concentrationsByWell: {[wellIndex: number]: Measurement} = {};
  for (const well of plateMap.wells) {
    const wellIndex = getFlattenedWellIndex(well.position.row, well.position.column, plateMap.size);
    const concentration = well.concentrationLayers?.[concentrationLayerId] ?? null;
    // Only update the unit if the well has an existing non-null measurement
    if (isNonNullMeasurement(concentration)) {
      concentrationsByWell[wellIndex] = {
        ...concentration,
        unit: convertHydratedUnitToMeasurementUnit(newUnit),
      };
    }
  }
  return concentrationsByWell;
}

/**
 * Calculate the preview plate map for concentration layers.
 *
 * This function performs two operations and merges the results:
 * 1. Updates units for ALL existing wells with this concentration layer
 * 2. Calculates new concentrations for the selected wells
 */
export function calculateConcentrationPreviewPlateMap(
  concentrationLayerData: ConcentrationLayerData,
  plateMap: PlateMap,
  selectedWellIndices: Set<number>
): PlateMap | null {
  const existingWellsWithUpdatedUnits = getExistingWellConcentrationsWithNewUnit(
    plateMap,
    concentrationLayerData.layerId,
    concentrationLayerData.concentrationUnit
  );
  const selectedWellsWithNewValues = calculateWellConcentrations(
    selectedWellIndices,
    plateMap.size,
    concentrationLayerData
  );
  // Selected wells override existing wells (new values take precedence)
  const mergedConcentrations = {
    ...existingWellsWithUpdatedUnits,
    ...selectedWellsWithNewValues,
  };
  return getPlateMapWithConcentrationPreview(plateMap, mergedConcentrations, concentrationLayerData.layerId);
}

/**
 * Get the indices of wells containing each reagent group in the plate map
 */
export function getWellIndicesByReagentGroupIds(
  plateMap: PlateMap,
  reagentGroupIds: Array<string>
): {[reagentGroupId: string]: Array<number>} {
  const wellIndicesByReagentGroupId: {[reagentGroupId: string]: Array<number>} = objectFromEntries(
    reagentGroupIds.map((id) => [id, []])
  );
  for (const [wellIndex, well] of plateMap.wells.entries()) {
    const wellGroupIds = new Set(well.sources.flatMap((source) => [...getGroupIdsForSource(source)]));
    for (const wellGroupId of wellGroupIds) {
      if (wellIndicesByReagentGroupId[wellGroupId] != null) {
        wellIndicesByReagentGroupId[wellGroupId].push(wellIndex);
      }
    }
  }
  return wellIndicesByReagentGroupId;
}

/**
 * Creates a transfer quantity layer for a specific reagent
 *
 * @param reagentId - The ID of the reagent
 * @returns A new TransferQuantityLayer with default values
 */
export function createEmptyTransferQuantityLayer(reagentId: string): TransferQuantityLayer {
  return {
    reagentId,
    quantityValue: null,
    quantityUnit: null,
  };
}

/**
 * Creates a final concentration layer for a specific reagent
 *
 * @param reagentId - The ID of the reagent
 * @returns A new FinalConcentrationLayer with default values
 */
export function createEmptyFinalConcentrationLayer(reagentId: string): FinalConcentrationLayer {
  return {
    reagentId,
    selectedConcentrationLayerId: null,
    concentrationSettings: {
      concentration: null,
      concentrationUnit: null,
      allowSetDilutionSettings: false,
      dilutionSettings: getDefaultDilutionSettings(),
    },
  };
}

/**
 * Checks if the given measurement attribute has mixed units across wells.
 */
export function hasMeasurementAttributeMixedUnits(
  plateMap: PlateMap,
  attribute: MeasurementAttribute
): boolean {
  const units = new Set<string>();
  for (const well of plateMap.wells) {
    const measurement = getMeasurementFromWell(well, attribute.type, attribute.key);
    // If we encounter MULTIPLE_VALUES_SENTINEL for units, it means there are already mixed units
    if (measurement.unit === MULTIPLE_VALUES_SENTINEL) {
      return true;
    }
    // Collect units from wells with valid measurements
    if (measurement.unit != null) {
      units.add(measurement.unit.symbol);
    }
  }
  return units.size > 1;
}

/**
 * Checks if well volumes have mixed units across the plate map.
 */
export function hasWellVolumeMixedUnits(plateMap: PlateMap): boolean {
  const units = new Set<string>();
  for (const well of plateMap.wells) {
    const quantity = well.quantity;
    if (isNonNullMeasurement(quantity)) {
      units.add(quantity.unit.symbol);
    }
  }
  return units.size > 1;
}

/**
 * Assumes that all wells have the same unit for the given measurement attribute and returns the unit.
 */
export function getUnitStringForMeasurementAttribute(
  plateMap: PlateMap,
  attribute: MeasurementAttribute
): string {
  const reagentId = attribute.key;
  const measurementType = attribute.type;

  for (const well of plateMap.wells) {
    const measurement = getMeasurementFromWell(well, measurementType, reagentId);
    if (
      measurement.value != null &&
      measurement.value !== MULTIPLE_VALUES_SENTINEL &&
      measurement.unit != null &&
      measurement.unit !== MULTIPLE_VALUES_SENTINEL
    ) {
      return measurement.unit.symbol;
    }
  }

  return '';
}

/**
 * Assumes that all wells have the same unit for the given concentration layer and returns the unit.
 *
 * @param plateMap - The plate map.
 * @param layerId - The ID of the concentration layer.
 * @returns The unit for the given concentration layer.
 */
export function getUnitStringForConcentrationLayer(plateMap: PlateMap, layerId: string): string {
  const firstConcentration = plateMap.wells
    .map((well) => well.concentrationLayers?.[layerId])
    .find((concentration) => isNonNullMeasurement(concentration));
  return firstConcentration?.unit.symbol || '';
}

/**
 * Gets the unit string for attribute.
 */
export function getUnitStringForAttribute(
  plateMap: PlateMap,
  attribute: PlateMapDisplayLayerAttribute,
  displayLayer: PlateMapWellDisplayLayer
): string {
  if (displayLayer === PlateMapWellDisplayLayer.CONCENTRATION) {
    return getUnitStringForConcentrationLayer(plateMap, (attribute as ConcentrationAttribute).key);
  } else if (displayLayer === PlateMapWellDisplayLayer.MEASUREMENT) {
    return getUnitStringForMeasurementAttribute(plateMap, attribute as MeasurementAttribute);
  } else if (displayLayer === PlateMapWellDisplayLayer.WELL_VOLUME) {
    const firstQuantity = plateMap.wells
      .map((well) => well.quantity)
      .find((quantity) => isNonNullMeasurement(quantity));
    return firstQuantity?.unit.symbol || '';
  }
  return '';
}

export function getReadonlyConcentrationLayersProps(plateMap: PlateMap): ConcentrationLayersProps {
  const layers = Object.values(plateMap.concentrationLayerInfoById ?? {});
  return {
    activeLayerId: null,
    concentrationLayers: layers.map((measurementLayer) =>
      createDefaultLayerSettings(measurementLayer.layerId, measurementLayer.name)
    ),
    enableAddRemoveLayers: false,
    onAddLayer: noop,
    onRemoveLayer: noop,
    onSelectLayer: noop,
  };
}
